// Edit-Fenster wie in Pro Tools (Reference Guide Kap. 26–31, 37–40):
// Edit-Modi Shuffle/Slip/Spot/Grid, Werkzeuge Smart Tool/Trim/Selector/Grabber/Pencil, Auswahl über mehrere Spuren,
// Lineal mit Markern und Bereichsauswahl, Clip Gain, Fades, Loops, Take- und Playlist-Lanes fürs Comping.
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { parseChord, parseKey, type Clip, type Folder, type Project, type Track } from '@srs/shared';
import { useStudio, projectLength } from '../state/store.ts';
import { useEdit, MODE_LABEL, TOOL_LABEL } from '../state/edit.ts';
import { engine } from '../audio/engine.ts';
import { emptyTrack, uid } from '../state/demo.ts';
import { resolveElastic } from '../audio/elastic.ts';
import { clipTransients, eventsOf } from '../audio/events.ts';
import { clipMap } from '../edit/warp.ts';
import { expandEdit, isFrozen, editGroupClips, groupMembers, groupToggle, groupVolume, groupsOfTrack, outputName, makeGroup, routeTracks, addBus } from '../edit/mixops.ts';
import { addWarp, moveWarp, removeWarp, setElastic } from '../edit/elasticops.ts';
import { STRETCH_MODES } from '../audio/stretch.ts';
import { freezeTracks, unfreezeTracks } from '../audio/render.ts';
import { barAtSec, barToBeat, bbt, beatAt, gridLines, meterAt, meterAtBar, meterEvents, secAt, secOfBar, secOfBBT, spbAt, tempoEvents } from '@srs/shared';
import { toast } from '../components/ui.tsx';
import { updatePresence } from '../collab/session.ts';
import { initials } from '../components/Topbar.tsx';
import { drawWave } from '../audio/waveform.ts';
import { importFiles, ACCEPT, TRACK_COLORS } from '../audio/importer.ts';
import { fadeCurve } from '../audio/segments.ts';
import * as op from '../edit/ops.ts';
import { removeTakes, removeTracks, selectedTrackIds, COMMANDS, keyLabel, run, currentRange } from '../edit/commands.ts';
import * as A from '../edit/automation.ts';
import { dbToPos, posToDb, toNorm, fromNorm, fmtParam } from '../components/controls.tsx';
import { bussesOf, pluginDef, slotLabel } from '../audio/plugins.ts';
import { PluginMenu, type Menu as PluginMenuState } from './Mixer.tsx';
import { SLOT_LETTERS } from '../state/mixer.ts';
import { channelsFor, inputInfo, inputLabel, isMonitoring, liveRec, onInputChange, setMonitoring } from '../audio/recorder.ts';
import { midiIn } from '../audio/midiin.ts';
import type { AutoMode } from '@srs/shared';

// ---- Lineale wie in Pro Tools: Takte|Schläge, Min:Sek, Tempo, Marker, Akkorde (einzeln ein- und ausblendbar) ----
type RulerId = 'bars' | 'minsec' | 'tempo' | 'markers' | 'chords';
const RULERS: { id: RulerId; label: string; h: number }[] = [
  { id: 'bars', label: 'Takte | Schläge', h: 24 }, { id: 'minsec', label: 'Min:Sek', h: 18 }, { id: 'tempo', label: 'Tempo', h: 18 },
  { id: 'markers', label: 'Marker', h: 22 }, { id: 'chords', label: 'Akkorde', h: 30 },
];
export interface RulerRow { id: RulerId; label: string; y: number; h: number }
export function rulerLayout(v: { minsec: boolean; tempo: boolean; chords: boolean }) {
  const rows: RulerRow[] = [];
  let y = 0;
  for (const r of RULERS) { if ((r.id === 'minsec' && !v.minsec) || (r.id === 'tempo' && !v.tempo) || (r.id === 'chords' && !v.chords)) continue; rows.push({ ...r, y }); y += r.h; }
  const get = (id: RulerId) => rows.find((r) => r.id === id);
  const mk = get('markers')!;
  return { rows, top: y, rulerEnd: mk.y + mk.h, get };
}
/** Farbe eines Abschnitts: eigene Farbe oder nach Name (Strophe, Pre, Refrain, Bridge …) */
export function markerColor(m: { name: string; color?: string }) {
  if (m.color) return m.color;
  const n = m.name.toLowerCase();
  if (/intro/.test(n)) return '#5a6478';
  if (/pre/.test(n)) return '#c7861b';
  if (/refrain|chorus|hook/.test(n)) return '#6b4fbb';
  if (/strophe|verse|vers/.test(n)) return '#d0543f';
  if (/bridge|c-teil|middle/.test(n)) return '#0f8b8d';
  if (/outro|ende|end/.test(n)) return '#003399';
  return '#2f5fd0';
}
/** Zeit unter der Maus für die Cursor-Anzeige im Zähler */
/** Einrastpunkt beim Ziehen im Grid-Modus: Sync Point bzw. erster Anschlag kurz nach dem Clip-Anfang (Audio) */
const syncAnchor = (p: Project, c: Clip) => op.syncOf(c, clipTransients(p, c));
export const editCursor = { t: null as number | null, y: null as number | null };
const COLORS = TRACK_COLORS;

// ---- Zeilen: Spuren und aufgeklappte Lanes (Takes, andere Playlists) ----
interface Lane { id: string; label: string; clips: Clip[]; takeId?: string; playlistId?: string }
type Row = { kind: 'track'; id: string; t: Track; y: number; h: number; nested?: boolean } | { kind: 'folder'; id: string; t: Track; f: Folder; kids: Track[]; y: number; h: number } | { kind: 'lane'; id: string; t: Track; lane: Lane; y: number; h: number } | { kind: 'auto'; id: string; t: Track; param: string; y: number; h: number };
export const autoId = (trackId: string, param: string) => `auto:${trackId}:${param}`;

export const laneId = (trackId: string, kind: 'take' | 'pl', id: string) => `lane:${trackId}:${kind}:${id}`;

function buildRows(p: Project, lanes: string[], laneH: number, autoLanes: Record<string, string[]> = {}): Row[] {
  const rows: Row[] = [];
  let y = 0;
  const emit = (t: Track, nested = false) => {
    rows.push({ kind: 'track', id: t.id, t, y, h: laneH, nested }); y += laneH;
    for (const param of autoLanes[t.id] ?? []) { const ah = Math.max(44, Math.round(laneH * 0.7)); rows.push({ kind: 'auto', id: autoId(t.id, param), t, param, y, h: ah }); y += ah; }
    if (!lanes.includes(t.id)) return;
    const lh = Math.max(36, Math.round(laneH * 0.72));
    for (const pl of p.playlists?.[t.id]?.others ?? []) {
      rows.push({ kind: 'lane', id: laneId(t.id, 'pl', pl.id), t, y, h: lh, lane: { id: laneId(t.id, 'pl', pl.id), label: `Playlist ${pl.name}`, clips: pl.clips.map((c) => ({ ...c, trackId: t.id })), playlistId: pl.id } }); y += lh;
    }
    for (const tk of p.takes.filter((x) => x.trackId === t.id).sort((a, b) => b.n - a.n)) {
      if (!tk.sourceId) continue;
      const c: Clip = { id: `take-${tk.id}`, trackId: t.id, kind: 'audio', name: `Take ${tk.n}`, start: tk.start, offset: tk.offset ?? 0, duration: tk.duration, sourceId: tk.sourceId, fadeIn: 0, fadeOut: 0, by: { userId: tk.by, at: tk.createdAt } };
      rows.push({ kind: 'lane', id: laneId(t.id, 'take', tk.id), t, y, h: lh, lane: { id: laneId(t.id, 'take', tk.id), label: `Take ${tk.n}`, clips: [c], takeId: tk.id } }); y += lh;
    }
  };
  const folders = new Map((p.folders ?? []).map((f) => [f.id, f]));
  const done = new Set<string>();
  for (const t of p.tracks) {
    if (t.kind === 'bus' || t.hidden) continue;
    const f = t.folderId ? folders.get(t.folderId) : undefined;
    if (!f) { emit(t); continue; }
    if (done.has(f.id)) continue;
    done.add(f.id);
    const kids = p.tracks.filter((x) => x.folderId === f.id && !x.hidden && x.kind !== 'bus');
    const fh = Math.max(34, Math.round(laneH * 0.62));
    rows.push({ kind: 'folder', id: `folder:${f.id}`, t: kids[0], f, kids, y, h: fh }); y += fh;
    if (f.open) for (const k of kids) emit(k, true);
  }
  // Master-Automation (Lautstärke, Plugin-Parameter der Master-Inserts) unter den Spuren
  if (autoLanes.master?.length) {
    const mt = A.masterTrack(p), ah = Math.max(44, Math.round(laneH * 0.7));
    for (const param of autoLanes.master) { rows.push({ kind: 'auto', id: autoId('master', param), t: mt, param, y, h: ah }); y += ah; }
  }
  return rows;
}

type Drag =
  | { kind: 'select'; row: Row; t0: number; t1: number; rowIdx0: number; rowIdx1: number; x0: number; moved: boolean; add?: string[]; toggle?: boolean; keep?: { start: number; end: number } }
  | { kind: 'move'; ids: string[]; anchor: string; x0: number; y0: number; dx: number; dy: number; copy: boolean; spot: boolean }
  | { kind: 'trim'; id: string; edge: 'start' | 'end'; x0: number; dx: number; loopTrim: boolean }
  | { kind: 'fade'; id: string; edge: 'in' | 'out'; x0: number; dx: number }
  | { kind: 'ruler'; t0: number; t1: number; x0: number; moved: boolean }
  | { kind: 'marker'; id: string; t0: number; x0: number; dx: number }
  | { kind: 'pencil'; trackId: string; t0: number; t1: number }
  | { kind: 'autoPt'; row: Extract<Row, { kind: 'auto' }>; idx: number; t: number; v: number; moved: boolean }
  | { kind: 'autoClick'; row: Extract<Row, { kind: 'auto' }>; t0: number; x0: number; y: number; moved: boolean }
  | { kind: 'autoDraw'; row: Extract<Row, { kind: 'auto' }>; pts: { t: number; v: number }[] }
  | { kind: 'scrub'; trackIds: string[] }
  | { kind: 'warp'; clipId: string; idx: number; t: number; moved: boolean }
  | null;

type DropHint = { x: number; y: number; kind: 'audio' | 'midi' | 'mixed' } | null;
type Ctx = { x: number; y: number; clipId?: string } | null;

export function Editor({ onEdit, editing }: { onEdit: (clipId: string) => void; editing: string | null }) {
  const project = useStudio((s) => s.project);
  const sel = useStudio((s) => s.selection);
  const commit = useStudio((s) => s.commit);
  const select = useStudio((s) => s.select);
  const zoom = useStudio((s) => s.zoom);
  const user = useStudio((s) => s.user);
  const ed = useEdit();
  const RL = rulerLayout(ed.view);
  const TOP = RL.top, RULER = RL.rulerEnd;
  const canvas = useRef<HTMLCanvasElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState({ x: 0, y: 0 });
  const scrollRef = useRef(scroll); scrollRef.current = scroll;
  const drag = useRef<Drag>(null);
  const [, force] = useState(0);
  const dropAt = useRef<DropHint>(null);
  const [dropping, setDropping] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<Ctx>(null);
  const [trackMenu, setTrackMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  useEffect(() => { const on = (e: Event) => setTrackMenu((e as CustomEvent).detail); window.addEventListener('srs:trackMenu', on); return () => window.removeEventListener('srs:trackMenu', on); }, []);
  const suppressCtx = useRef(false);
  /** Spur-Ids in der Reihenfolge der Spuren */
  const orderTracks = (ids: string[]) => useStudio.getState().project.tracks.map((t) => t.id).filter((id) => ids.includes(id)).concat(ids.filter((id) => id.includes(':')));
  const [laneSolo, setLaneSolo] = useState<Record<string, string>>({});
  const fileInput = useRef<HTMLInputElement>(null);
  const rows = useMemo(() => buildRows(project, ed.lanes, ed.laneH, ed.autoLanes), [project, ed.lanes, ed.laneH, ed.autoLanes]);
  const rowsRef = useRef(rows); rowsRef.current = rows;
  const contentH = rows.length ? rows[rows.length - 1].y + rows[rows.length - 1].h : 0;

  const xOf = (sec: number) => sec * zoom - scrollRef.current.x;
  const secOf = (x: number) => (x + scrollRef.current.x) / zoom;
  const rowAt = (y: number) => rowsRef.current.findIndex((r) => y >= TOP + r.y - scrollRef.current.y && y < TOP + r.y + r.h - scrollRef.current.y);
  const yRow = (r: Row) => TOP + r.y - scrollRef.current.y;
  const trackRows = () => rowsRef.current.filter((r) => r.kind === 'track') as Extract<Row, { kind: 'track' }>[];

  /** Raster: im Grid-Modus oder mit Snap; ⌘/Strg kehrt das für eine Aktion um (wie Pro Tools) */
  const snapT = (t: number, e?: { metaKey: boolean; ctrlKey: boolean }) => {
    const on = (ed.mode === 'grid' || ed.snap) !== !!(e && (e.metaKey || e.ctrlKey));
    return Math.max(0, on ? op.snapGrid(useStudio.getState().project, t, ed.grid) : t);
  };

  // Scrollen von außen (Zoom auf Auswahl) und Mitlaufen
  useEffect(() => {
    const h = (e: Event) => setScroll((s) => ({ ...s, x: Math.max(0, (e as CustomEvent<number>).detail * useStudio.getState().zoom) }));
    window.addEventListener('srs:scrollTo', h);
    return () => window.removeEventListener('srs:scrollTo', h);
  }, []);
  // Comping-Befehl (⌥⌃V)
  useEffect(() => {
    const h = () => compSelection();
    window.addEventListener('srs:comp', h);
    return () => window.removeEventListener('srs:comp', h);
  });
  // Lane-Solo an die Engine geben
  useEffect(() => {
    engine.laneOverride.clear();
    for (const [tid, lid] of Object.entries(laneSolo)) {
      const row = rows.find((r) => r.id === lid);
      if (row?.kind === 'lane') engine.laneOverride.set(tid, row.lane.clips);
    }
    engine.reschedule();
  }, [laneSolo, rows]);

  // ---- Zeichnen ----
  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const c = canvas.current, w = wrap.current;
      if (c && w) {
        const dpr = devicePixelRatio || 1, W = w.clientWidth, H = w.clientHeight;
        if (c.width !== W * dpr || c.height !== H * dpr) { c.width = W * dpr; c.height = H * dpr; c.style.width = `${W}px`; c.style.height = `${H}px`; }
        const g = c.getContext('2d')!;
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        const st = useStudio.getState();
        if (st.transport.playing && useEdit.getState().follow && !drag.current) {
          const px = engine.position * st.zoom - scrollRef.current.x;
          if (px > W * 0.92 || px < 0) setScroll((s) => ({ ...s, x: Math.max(0, engine.position * st.zoom - W * 0.08) }));
        }
        paint(g, W, H, st.project);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  });

  function paint(g: CanvasRenderingContext2D, W: number, H: number, p: Project) {
    const a0 = Math.max(0, secOf(0)), a1 = secOf(W);
    const spb = spbAt(p, a0), bar = spb * meterAt(p, a0).barLen, E = useEdit.getState(), range = E.range, d = drag.current;
    g.clearRect(0, 0, W, H);
    g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);
    // Zeilen
    let zebra = 0;
    for (const r of rowsRef.current) {
      if (r.kind === 'track') zebra++;
      const y = yRow(r);
      if (y > H || y + r.h < TOP) continue;
      g.fillStyle = r.kind === 'folder' ? '#f6f7fa' : r.kind === 'lane' ? '#f5f6f9' : r.kind === 'auto' ? '#fbfaff' : sel.trackId === r.id || range?.trackIds.includes(r.id) ? '#eef3fc' : zebra % 2 ? '#fff' : '#fafbfc';
      g.fillRect(0, y, W, r.h);
      g.fillStyle = r.kind === 'track' ? '#e3e7ee' : '#eceef3'; g.fillRect(0, y + r.h - 1, W, 1);
    }
    // Raster: Takte, Schläge, im Grid-Modus feiner
    // Raster folgt Tempo- und Taktwechseln
    const gq = op.gridSec(p, E.grid, a0), fine = (E.mode === 'grid' || E.snap) && gq * zoom >= 8 ? gq : 0;
    if (fine && fine < spb) for (const t of gridLines(p, a0, a1, E.grid)) { g.fillStyle = '#f6f7f9'; g.fillRect(Math.round(xOf(t)), TOP, 1, H); }
    for (let b = Math.max(0, Math.floor(barAtSec(p, a0))); ; b++) {
      const t0 = secOfBar(p, b);
      if (xOf(t0) > W) break;
      g.fillStyle = '#e1e5ec'; g.fillRect(Math.round(xOf(t0)), TOP, 1, H);
      if (spbAt(p, t0) * zoom < 12) continue;
      const m = meterAtBar(p, b), b0 = barToBeat(p, b);
      for (let k = 1; k < m.num; k++) { g.fillStyle = '#f0f2f5'; g.fillRect(Math.round(xOf(secAt(p, b0 + k * 4 / m.den))), TOP, 1, H); }
    }
    if (p.loop.enabled) { g.fillStyle = 'rgba(0,51,153,.04)'; g.fillRect(xOf(p.loop.start), TOP, (p.loop.end - p.loop.start) * zoom, H); }
    g.save(); g.beginPath(); g.rect(0, TOP, W, H - TOP); g.clip();
    // Clips der Spuren (mit Vorschau laufender Aktionen)
    const moving = d?.kind === 'move' ? d : null;
    const trs = trackRows();
    const preview = (c: Clip): { c: Clip; row: Row | undefined } => {
      const row = rowsRef.current.find((r) => r.kind === 'track' && r.id === c.trackId);
      if (moving && moving.ids.includes(c.id) && !c.locked && !moving.spot) {
        const anchor = p.clips.find((x) => x.id === moving.anchor)!;
        const so = syncAnchor(p, anchor), dt = snapT(anchor.start + so + moving.dx / zoom) - (anchor.start + so);
        const ti = trs.findIndex((r) => r.id === c.trackId), dRows = Math.round(moving.dy / ed.laneH);
        const target = trs[Math.max(0, Math.min(trs.length - 1, ti + dRows))];
        return { c: { ...c, start: Math.max(0, c.start + dt) }, row: target && (target.t.kind === 'midi') === (c.kind === 'midi') ? target : row };
      }
      if (d?.kind === 'trim' && d.id === c.id) return { c: trimPreview(c, d), row };
      if (d?.kind === 'fade' && d.id === c.id) return { c: fadePreview(c, d), row };
      return { c, row };
    };
    for (const c of p.clips) {
      if (moving?.copy && moving.ids.includes(c.id)) drawOne(g, c, rowsRef.current.find((r) => r.id === c.trackId), W, p, false, 0.5);
      const { c: pc, row } = preview(c);
      drawOne(g, pc, row, W, p, sel.clipIds.includes(c.id) || c.id === editing, moving?.ids.includes(c.id) ? 0.85 : 1);
    }
    // Sync Points: kleines Dreieck unten im Clip
    for (const c of p.clips) {
      if (c.syncPoint === undefined) continue;
      const { c: pc, row } = preview(c);
      if (!row) continue;
      const sx = Math.round(xOf(pc.start + (c.syncPoint ?? 0))) + 0.5, by = yRow(row) + row.h - 3;
      if (sx < -6 || sx > W + 6) continue;
      g.fillStyle = '#071026'; g.beginPath(); g.moveTo(sx - 5, by); g.lineTo(sx + 5, by); g.lineTo(sx, by - 7); g.closePath(); g.fill();
    }
    // Ziehen im Grid: Linie am Einrastpunkt (Anschlag) des gezogenen Clips
    if (moving && (ed.mode === 'grid' || ed.snap)) {
      const an = p.clips.find((x) => x.id === moving.anchor);
      if (an && an.kind === 'audio') {
        const { c: pc, row } = preview(an), so = syncAnchor(p, an);
        if (row && so > 0) { const ax = Math.round(xOf(pc.start + so)) + 0.5; g.fillStyle = '#e23e2c'; g.fillRect(ax - 0.5, yRow(row) + 2, 1.5, row.h - 5); }
      }
    }
    // Schere: Schnittlinie unter der Maus
    if (ed.tool === 'cut' && editCursor.t !== null && editCursor.y !== null && !d) {
      const row = rowsRef.current[rowAt(editCursor.y)];
      if (row?.kind === 'track') {
        const t = snapT(editCursor.t), cx = Math.round(xOf(t)) + 0.5, hit = p.clips.some((c) => c.trackId === row.id && t > c.start && t < op.clipEnd(c));
        g.setLineDash([3, 3]); g.strokeStyle = hit ? '#e23e2c' : 'rgba(7,16,38,.35)'; g.lineWidth = 1.5;
        g.beginPath(); g.moveTo(cx, yRow(row) + 2); g.lineTo(cx, yRow(row) + row.h - 3); g.stroke(); g.setLineDash([]);
      }
    }
    // Clip Groups: gestrichelter Rahmen um alle Clips einer Gruppe
    const groups = new Map<string, Clip[]>();
    for (const c of p.clips) if (c.groupId) groups.set(c.groupId, [...(groups.get(c.groupId) ?? []), preview(c).c]);
    for (const [, cs] of groups) {
      const rs = cs.map((c) => rowsRef.current.find((r) => r.kind === 'track' && r.id === c.trackId)).filter(Boolean) as Row[];
      if (!rs.length) continue;
      const x0 = xOf(Math.min(...cs.map((c) => c.start))), x1 = xOf(Math.max(...cs.map(op.clipEnd)));
      const y0 = Math.min(...rs.map((r) => yRow(r))) + 2, y1 = Math.max(...rs.map((r) => yRow(r) + r.h)) - 3;
      g.setLineDash([5, 3]); g.strokeStyle = '#0f8b8d'; g.lineWidth = 1.5; g.strokeRect(x0 - 2, y0, x1 - x0 + 4, y1 - y0); g.setLineDash([]);
      g.font = '700 9px "IBM Plex Sans"'; const tw = g.measureText('GRUPPE').width; g.fillStyle = '#0f8b8d'; g.fillRect(x1 - tw - 8, y0, tw + 8, 12); g.fillStyle = '#fff'; g.fillText('GRUPPE', x1 - tw - 4, y0 + 9);
    }
    // Ordner: Clips der Spuren darin als schmale Streifen (wie Pro Tools)
    for (const r of rowsRef.current) if (r.kind === 'folder') {
      const y0 = yRow(r);
      if (y0 > H || y0 + r.h < TOP) continue;
      g.fillStyle = mix(r.f.color, 0.07); g.fillRect(0, y0, W, r.h - 1);
      const n = Math.max(1, r.kids.length), lh = Math.max(2, Math.min(7, (r.h - 10) / n));
      r.kids.forEach((k, i) => {
        g.fillStyle = mix(k.color, k.mute ? 0.3 : 0.75);
        for (const c of p.clips) if (c.trackId === k.id) { const x = xOf(c.start), w = c.duration * zoom; if (x > W || x + w < 0) continue; g.fillRect(x + 1, y0 + 5 + i * lh, Math.max(1, w - 2), Math.max(1.5, lh - 1)); }
      });
      if (range && r.kids.every((k) => range.trackIds.includes(k.id)) && !op.isEmptyRange(range)) { g.fillStyle = 'rgba(0,51,153,.16)'; g.fillRect(xOf(range.start), y0, (range.end - range.start) * zoom, r.h - 1); }
    }
    // Lanes
    for (const r of rowsRef.current) if (r.kind === 'lane') for (const c of r.lane.clips) drawOne(g, c, r, W, p, false, laneSolo[r.t.id] === r.id ? 1 : 0.75);
    // Laufende Aufnahme: roter Balken wächst mit, darin die Live-Wellenform (wie in Pro Tools)
    if (liveRec.on && useStudio.getState().transport.recording) {
      const pos = engine.position, L = liveRec.loop;
      const a = L ? (pos >= L.start ? L.start : NaN) : liveRec.start, b = Math.min(pos, liveRec.punchOut ?? Infinity);
      if (b > a) for (const r of rowsRef.current) {
        if (r.kind !== 'track' || !liveRec.trackIds.includes(r.id)) continue;
        const y = yRow(r), x0 = xOf(a), x1 = xOf(b), hh = r.h - 6;
        roundRect(g, x0, y + 3, Math.max(2, x1 - x0), hh, 4); g.fillStyle = 'rgba(226,62,44,.16)'; g.fill(); g.strokeStyle = '#e23e2c'; g.lineWidth = 1.5; g.stroke();
        g.fillStyle = '#e23e2c'; g.fillRect(x0, y + 3, Math.max(2, x1 - x0), 13);
        g.font = '600 10px "IBM Plex Sans"'; g.fillStyle = '#fff'; if (x1 - x0 > 60) g.fillText('● Aufnahme', x0 + 6, y + 13);
        const bi = liveRec.bridgeIdx[r.id], isBrowser = liveRec.browserIds.includes(r.id);
        if (isBrowser || (bi !== undefined && bi >= 0)) {
          const chans = isBrowser ? channelsFor(r.t, inputInfo.channels || 1) : [bi], mid = y + 16 + (hh - 13) / 2, amp = (hh - 17) / 2;
          g.fillStyle = 'rgba(160,32,20,.75)';
          for (const w of isBrowser ? liveRec.wave : liveRec.bridgeWave) {
            if (w.t < a || w.t > b) continue;
            const pk = Math.min(1, Math.max(...chans.map((c) => w.pk[c] ?? (isBrowser ? w.pk[0] : 0) ?? 0)));
            const wx = xOf(w.t), hgt = Math.max(1, pk * amp);
            g.fillRect(wx, mid - hgt, Math.max(1, 0.045 * zoom), hgt * 2);
          }
        }
      }
    }
    // Bridge-Takes nach dem Stopp: Platzhalter mit Zustand (gesichert, übertragen, Fehler)
    for (const r of rowsRef.current) {
      const pd = r.kind === 'track' ? liveRec.pending[r.id] : undefined;
      if (!pd || pd.b <= pd.a) continue;
      const y = yRow(r), x0 = xOf(pd.a), x1 = xOf(pd.b), hh = r.h - 6, col = pd.err ? '#e23e2c' : '#5b6b8c';
      roundRect(g, x0, y + 3, Math.max(2, x1 - x0), hh, 4); g.fillStyle = pd.err ? 'rgba(226,62,44,.10)' : 'rgba(91,107,140,.14)'; g.fill();
      g.setLineDash([4, 3]); g.strokeStyle = col; g.lineWidth = 1.5; g.stroke(); g.setLineDash([]);
      g.font = '600 10px "IBM Plex Sans"';
      if (pd.err) {
        // Fehler vollständig lesbar, auch wenn der Bereich kurz ist
        const tw = g.measureText(pd.text).width + 12;
        roundRect(g, x0, y + 5, tw, 15, 3); g.fillStyle = '#e23e2c'; g.fill();
        g.fillStyle = '#fff'; g.fillText(pd.text, x0 + 6, y + 16);
      } else {
        g.save(); g.beginPath(); g.rect(x0, y, Math.max(2, x1 - x0), r.h); g.clip();
        g.fillStyle = col; g.fillText(pd.text, x0 + 6, y + 15);
        g.restore();
      }
    }
    // Warp-Ansicht: Event-Marker (grau, erkannte Anschläge) und Warp-Marker (blau, ziehbar)
    for (const r of rowsRef.current) {
      if (r.kind !== 'track' || !E.warp.includes(r.id) || !r.t.elastic) continue;
      const y = yRow(r);
      for (const c of p.clips) {
        if (c.trackId !== r.id || c.kind !== 'audio' || xOf(c.start) > W || xOf(c.start + c.duration) < 0) continue;
        const m = clipMap(p, c), ev = c.sourceId ? eventsOf(c.sourceId) : null, x0 = xOf(c.start), x1 = xOf(c.start + c.duration);
        g.save(); g.beginPath(); g.rect(x0, y, x1 - x0, r.h); g.clip();
        g.fillStyle = 'rgba(7,16,38,.35)';
        for (const s of ev ?? []) { const ex = Math.round(xOf(m.toTime(s))) + 0.5; if (ex > x0 && ex < x1) g.fillRect(ex, y + r.h * 0.45, 1, r.h * 0.5); }
        const b0 = beatAt(p, c.start);
        (c.warp?.points ?? []).forEach((w, i) => {
          const dragging = d?.kind === 'warp' && d.clipId === c.id && d.idx === i;
          const t = dragging ? d.t : secAt(p, b0 + w.b), wx = Math.round(xOf(t)) + 0.5;
          if (t <= c.start + 1e-4 || t >= c.start + c.duration - 1e-4) return;
          g.fillStyle = dragging ? '#001f5c' : '#003399'; g.fillRect(wx - 0.5, y + 2, 2, r.h - 4);
          g.beginPath(); g.moveTo(wx - 5, y + 2); g.lineTo(wx + 5, y + 2); g.lineTo(wx, y + 9); g.closePath(); g.fill();
        });
        g.restore();
      }
    }
    // Automation
    for (const r of rowsRef.current) if (r.kind === 'auto') drawAuto(g, r, W, d);
    // Strip-Silence-Vorschau: Rechtecke um das, was bleibt
    if (E.stripPreview) for (const [cid, regs] of E.stripPreview) {
      const c = p.clips.find((x) => x.id === cid), row = c && rowsRef.current.find((r) => r.id === c.trackId);
      if (!c || !row) continue;
      g.strokeStyle = '#c7861b'; g.lineWidth = 1.5; g.fillStyle = 'rgba(199,134,27,.10)';
      for (const [a, b] of regs) { const x = xOf(a), w = (b - a) * zoom; g.fillRect(x, yRow(row) + 3, w, row.h - 7); g.strokeRect(x + 0.5, yRow(row) + 3.5, w - 1, row.h - 8); }
    }
    // Beat-Detective-Vorschau: Trigger über den gewählten Spuren (Takt kräftig, Schlag, Unterteilung zart), Ziel beim Conform
    if (E.beatPreview && range) {
      const rows = rowsRef.current.filter((r) => range.trackIds.includes(r.id));
      for (const tr of E.beatPreview) {
        const x = Math.round(xOf(tr.t)) + 0.5;
        if (x < -2 || x > W + 2) continue;
        g.strokeStyle = tr.cls === 'bar' ? '#c2410c' : tr.cls === 'beat' ? '#e0731a' : 'rgba(224,115,26,.55)';
        g.lineWidth = tr.cls === 'bar' ? 2 : 1;
        for (const r of rows) {
          const y = yRow(r);
          g.beginPath(); g.moveTo(x, y + 2); g.lineTo(x, y + r.h - 3); g.stroke();
          if (tr.to !== undefined && Math.abs(tr.to - tr.t) > 0.0005) {
            const x2 = xOf(tr.to);
            g.fillStyle = '#003399'; g.beginPath(); g.moveTo(x2, y + 3); g.lineTo(x2 - 4, y - 3 + 3); g.lineTo(x2 + 4, y - 3 + 3); g.closePath(); g.fill();
            g.strokeStyle = 'rgba(0,51,153,.6)'; g.lineWidth = 1; g.beginPath(); g.moveTo(x, y + 4); g.lineTo(x2, y + 4); g.stroke();
          }
        }
      }
    }
    // Pencil: neuer MIDI-Clip
    if (d?.kind === 'pencil') { const row = rowsRef.current.find((r) => r.id === d.trackId); if (row) { const a = Math.min(d.t0, d.t1), b = Math.max(d.t0, d.t1); g.setLineDash([4, 3]); g.strokeStyle = '#003399'; g.strokeRect(xOf(a), yRow(row) + 5, (b - a) * zoom, row.h - 11); g.setLineDash([]); } }
    // Auswahl
    if (range) {
      for (const r of rowsRef.current) {
        if (!range.trackIds.includes(r.id)) continue;
        const y = yRow(r);
        if (op.isEmptyRange(range)) { g.fillStyle = '#071026'; g.fillRect(Math.round(xOf(range.start)), y + 2, 1.5, r.h - 4); }
        else { g.fillStyle = 'rgba(0,51,153,.16)'; g.fillRect(xOf(range.start), y, (range.end - range.start) * zoom, r.h - 1); }
      }
    }
    // Ziel beim Ziehen von Dateien
    const da = dropAt.current;
    if (da) {
      const ri = rowAt(da.y), r = rowsRef.current[ri];
      const time = da.x < 0 ? engine.position : snapT(secOf(da.x)), x = xOf(time);
      const ly = r ? yRow(r) : TOP + contentH - scrollRef.current.y, lh = r?.h ?? ed.laneH;
      g.fillStyle = 'rgba(0,51,153,.07)'; g.fillRect(0, ly, W, lh);
      g.fillStyle = '#003399'; g.fillRect(Math.max(0, x), TOP, 2, H);
      g.setLineDash([5, 4]); g.strokeStyle = '#003399'; g.lineWidth = 1.5; g.strokeRect(1, ly + 1, W - 2, lh - 2); g.setLineDash([]);
      const t = r?.kind === 'track' ? r.t : undefined;
      const fits = t && (da.kind === 'mixed' || (da.kind === 'midi') === (t.kind === 'midi'));
      const label = `Takt ${fmtBar(p, time)} · ${fits ? `auf ${t!.name}` : 'neue Spur'}`;
      g.font = '600 12px "IBM Plex Sans"'; const tw = g.measureText(label).width;
      const lx = Math.min(Math.max(8, x + 10), W - tw - 26);
      roundRect(g, lx, ly + lh / 2 - 13, tw + 18, 26, 6); g.fillStyle = '#003399'; g.fill();
      g.fillStyle = '#fff'; g.fillText(label, lx + 9, ly + lh / 2 + 4);
    }
    g.restore();
    // ---- Lineale ----
    g.fillStyle = '#fbfcfd'; g.fillRect(0, 0, W, TOP);
    for (const row of RL.rows) { g.fillStyle = '#e9ecf1'; g.fillRect(0, row.y + row.h - 1, W, 1); }
    g.fillStyle = '#d5dae3'; g.fillRect(0, TOP - 1, W, 1);
    const BR = RL.get('bars')!, MS = RL.get('minsec'), TE = RL.get('tempo'), MK = RL.get('markers')!, CH = RL.get('chords');
    const rr = d?.kind === 'ruler' ? { a: Math.min(d.t0, d.t1), b: Math.max(d.t0, d.t1) } : range && !op.isEmptyRange(range) ? { a: range.start, b: range.end } : null;
    if (rr) { g.fillStyle = 'rgba(0,51,153,.13)'; g.fillRect(xOf(rr.a), 0, (rr.b - rr.a) * zoom, (MS ? MS.y + MS.h : BR.h) - 1); }
    if (p.loop.enabled) { g.fillStyle = '#7f9be0'; g.fillRect(xOf(p.loop.start), BR.h - 4, (p.loop.end - p.loop.start) * zoom, 3); }
    // Takte und Schläge
    g.font = '500 11px "IBM Plex Mono"';
    const step = Math.max(1, Math.ceil(46 / (bar * zoom)));
    for (let b = Math.max(0, Math.floor(barAtSec(p, a0))); ; b++) {
      const t0 = secOfBar(p, b), x = Math.round(xOf(t0));
      if (x > W) break;
      if (b % step === 0) { g.fillStyle = '#9aa3b5'; g.fillRect(x, 3, 1, BR.h - 4); g.fillStyle = '#2b3446'; g.fillText(String(b + 1), x + 4, 15); }
      if (step === 1 && spbAt(p, t0) * zoom >= 9) { const m = meterAtBar(p, b), b0 = barToBeat(p, b); for (let k = 1; k < m.num; k++) { g.fillStyle = '#c8ced9'; g.fillRect(Math.round(xOf(secAt(p, b0 + k * 4 / m.den))), BR.h - 6, 1, 5); } }
    }
    // Minuten:Sekunden
    if (MS) {
      const st = [1, 2, 5, 10, 15, 30, 60, 120].find((x) => x * zoom >= 58) ?? 120;
      g.font = '500 10px "IBM Plex Mono"';
      for (let t = Math.max(0, Math.floor(secOf(0) / st) * st); xOf(t) < W; t += st) {
        const x = Math.round(xOf(t));
        g.fillStyle = '#b4bccb'; g.fillRect(x, MS.y + 3, 1, MS.h - 4);
        g.fillStyle = '#5a6478'; g.fillText(`${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`, x + 4, MS.y + 13);
      }
    }
    // Tempo- und Taktart-Spur: jedes Ereignis als Fähnchen, Doppelklick setzt ein neues, Klick bearbeitet
    if (TE) {
      const my = TE.y + TE.h / 2;
      g.fillStyle = 'rgba(15,139,141,.35)'; g.fillRect(Math.max(0, xOf(0)), my, W, 1);
      const evs: { sec: number; txt: string; kind: 't' | 'm' }[] = [...tempoEvents(p).map((e) => ({ sec: e.sec, txt: `♩ ${String(e.bpm).replace('.', ',')}`, kind: 't' as const })), ...meterEvents(p).map((e) => ({ sec: e.sec, txt: `${e.num}/${e.den}`, kind: 'm' as const }))].sort((x, y) => x.sec - y.sec || (x.kind === 't' ? -1 : 1));
      // Tempo und Taktart an derselben Stelle zu einer Beschriftung zusammenfassen
      for (let i = evs.length - 1; i > 0; i--) if (Math.abs(evs[i].sec - evs[i - 1].sec) < 1e-6) { evs[i - 1] = { ...evs[i - 1], txt: `${evs[i - 1].txt}  ${evs[i].txt}` }; evs.splice(i, 1); }
      g.font = '600 10px "IBM Plex Mono"';
      // Rauten immer, Beschriftung nur, wo Platz ist (bei vielen Wechseln, z. B. aus Beat Detective, sonst unlesbar)
      for (let i = 0; i < evs.length; i++) {
        const e = evs[i], x = xOf(e.sec);
        if (x > W) break;
        const tw = g.measureText(e.txt).width, nx = i + 1 < evs.length ? xOf(evs[i + 1].sec) : Infinity;
        if (Math.min(nx, W + 200) < -10) continue;
        g.beginPath(); g.moveTo(x, my - 4); g.lineTo(x + 4, my); g.lineTo(x, my + 4); g.lineTo(x - 4, my); g.closePath();
        g.fillStyle = e.kind === 't' ? '#0f8b8d' : '#6b4fbb'; g.fill();
        if (nx - x < tw + 16) continue;
        const lx = Math.max(x + 7, 2);
        g.fillStyle = '#fbfcfd'; g.fillRect(lx, TE.y + 2, tw + 8, TE.h - 4);
        g.fillStyle = e.kind === 't' ? '#0b6b6d' : '#5a3fa0'; g.fillText(e.txt, lx + 4, TE.y + 12.5);
      }
    }
    // Marker als farbige Abschnitte
    const songEnd = Math.max(...p.clips.map(op.clipEnd), bar * 8);
    const mks = p.markers.map((m) => ({ m, t: d?.kind === 'marker' && d.id === m.id ? snapT(d.t0 + d.dx / zoom) : m.time })).sort((a, b) => a.t - b.t);
    mks.forEach(({ m, t }, i) => {
      const end = m.end ?? mks[i + 1]?.t ?? Math.max(songEnd, t + bar * 4), x = xOf(t), w = Math.max(0, (end - t) * zoom);
      if (x > W || x + w < 0) return;
      const col = markerColor(m), y0 = MK.y + 3, hh = MK.h - 6;
      g.fillStyle = mix(col, 0.16); g.fillRect(x, y0, w, hh);
      g.fillStyle = col; g.fillRect(x, y0, 2, hh);
      g.beginPath(); g.moveTo(x + 9, y0 + hh / 2 - 4); g.lineTo(x + 13, y0 + hh / 2); g.lineTo(x + 9, y0 + hh / 2 + 4); g.lineTo(x + 5, y0 + hh / 2); g.closePath(); g.fill();
      g.save(); g.beginPath(); g.rect(x, y0, Math.max(0, w - 2), hh); g.clip();
      g.font = '600 10.5px "IBM Plex Sans"'; g.fillStyle = shade(col); g.fillText(m.name, x + 17, y0 + hh / 2 + 4); g.restore();
    });
    // Akkordspur
    if (CH) {
      const key = safeKey(p.key);
      p.chords.forEach((ch, k) => {
        const end = p.chords[k + 1]?.time ?? ch.time + bar * 2, x = xOf(ch.time) + 2, w = (end - ch.time) * zoom - 4;
        if (x > W || x + w < 0) return;
        const playing = engine.position >= ch.time && engine.position < end;
        roundRect(g, x, CH.y + 4, w, CH.h - 9, 4);
        g.fillStyle = playing ? '#eaeffb' : '#fff'; g.fill(); g.strokeStyle = playing ? '#9fb6ea' : '#e3e7ee'; g.lineWidth = 1; g.stroke();
        g.font = '600 12px "IBM Plex Sans"'; g.fillStyle = '#071026'; g.fillText(ch.symbol, x + 8, CH.y + 19);
        const sw = g.measureText(ch.symbol).width;
        g.font = '500 10px "IBM Plex Mono"'; g.fillStyle = '#8a93a6'; g.fillText(roman(ch.symbol, key), x + 14 + sw, CH.y + 19);
      });
    }
    for (const peer of useStudio.getState().peers) if (peer.cursor) {
      const x = xOf(peer.cursor.time);
      g.fillStyle = peer.user.color; g.fillRect(x, TOP, 1.5, H);
      g.beginPath(); g.arc(x, 12, 9, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#fff'; g.font = '700 8.5px "IBM Plex Sans"'; g.textAlign = 'center'; g.fillText(initials(peer.user.name), x, 15); g.textAlign = 'left';
    }
    const ph = Math.round(xOf(engine.position));
    g.fillStyle = useStudio.getState().transport.recording ? '#e23e2c' : '#003399';
    g.fillRect(ph, 0, 2, H);
    g.beginPath(); g.moveTo(ph - 6, 0); g.lineTo(ph + 8, 0); g.lineTo(ph + 1, 9); g.closePath(); g.fill();
  }

  // ---- Automation-Lanes ----
  const autoNorm = (r: Row, param: string, v: number) => { const def = A.isInsParam(param) ? A.paramInfo(param, r.t).def : undefined; return def ? Math.max(0, Math.min(1, toNorm(def, v))) : param === 'pan' ? (v + 1) / 2 : param === 'mute' ? v : dbToPos(Math.max(-96, v <= -59.9 ? -96 : v)); };
  const autoY = (r: Row, param: string, v: number) => yRow(r) + 6 + (1 - autoNorm(r, param, v)) * (r.h - 13);
  const autoV = (r: Row, param: string, y: number) => {
    const n = Math.max(0, Math.min(1, 1 - (y - yRow(r) - 6) / (r.h - 13)));
    const def = A.isInsParam(param) ? A.paramInfo(param, r.t).def : undefined;
    if (def) return def.bool ? (n >= 0.5 ? 1 : 0) : fromNorm(def, n);
    return param === 'pan' ? Math.round((n * 2 - 1) * 100) / 100 : param === 'mute' ? (n >= 0.5 ? 1 : 0) : Math.round((n <= 0.01 ? -60 : Math.max(-60, posToDb(n))) * 10) / 10;
  };
  function autoPointsFor(r: Extract<Row, { kind: 'auto' }>, d: Drag) {
    const pts = A.points(r.t, r.param);
    if (d?.kind === 'autoPt' && d.row.id === r.id) return pts.map((p, i) => (i === d.idx ? { t: d.t, v: d.v } : p));
    if (d?.kind === 'autoDraw' && d.row.id === r.id && d.pts.length > 1) {
      const s = Math.min(...d.pts.map((p) => p.t)), e = Math.max(...d.pts.map((p) => p.t));
      return [...pts.filter((p) => p.t < s || p.t > e), ...d.pts].sort((a, b) => a.t - b.t);
    }
    return pts;
  }
  function drawAuto(g: CanvasRenderingContext2D, r: Extract<Row, { kind: 'auto' }>, W: number, d: Drag) {
    const y0 = yRow(r);
    if (y0 > (wrap.current?.clientHeight ?? 0) || y0 + r.h < TOP) return;
    const pts = autoPointsFor(r, d), on = (r.t.automation?.mode ?? 'off') !== 'off';
    const info = A.paramInfo(r.param, r.t), stepped = r.param === 'mute' || !!info.step || !!info.choices;
    g.fillStyle = '#fbfaff'; g.fillRect(0, y0, W, r.h - 1);
    if (r.param === 'pan') { g.fillStyle = '#eceef3'; g.fillRect(0, Math.round(autoY(r, 'pan', 0)), W, 1); }
    const col = on ? '#6b4fbb' : '#a9a1c8';
    if (!pts.length) {
      g.setLineDash([4, 4]); g.strokeStyle = '#b8aedb'; g.lineWidth = 1.2;
      const y = autoY(r, r.param, A.staticValue(r.t, r.param));
      g.beginPath(); g.moveTo(0, y); g.lineTo(W, y); g.stroke(); g.setLineDash([]);
      g.font = '500 10.5px "IBM Plex Sans"'; g.fillStyle = '#8a93a6'; g.fillText('Klick setzt einen Punkt, Ziehen wählt einen Bereich, Pencil zeichnet', 12, y0 + 14);
      return;
    }
    g.beginPath();
    const x0 = 0, xl = W;
    g.moveTo(x0, autoY(r, r.param, A.valueAt(pts, secOf(x0), r.param, 0)));
    for (const p of pts) {
      const x = xOf(p.t);
      if (x < 0) continue;
      if (x > W) break;
      if (stepped) g.lineTo(x, autoY(r, r.param, A.valueAt(pts, p.t - 1e-4, r.param, 0)));
      g.lineTo(x, autoY(r, r.param, p.v));
    }
    g.lineTo(xl, autoY(r, r.param, A.valueAt(pts, secOf(xl), r.param, 0)));
    g.strokeStyle = col; g.lineWidth = 1.6; g.stroke();
    g.lineTo(xl, y0 + r.h - 1); g.lineTo(x0, y0 + r.h - 1); g.closePath(); g.fillStyle = on ? 'rgba(107,79,187,.08)' : 'rgba(107,79,187,.03)'; g.fill();
    for (const p of pts) {
      const x = xOf(p.t);
      if (x < -4 || x > W + 4) continue;
      g.beginPath(); g.arc(x, autoY(r, r.param, p.v), 3.5, 0, Math.PI * 2); g.fillStyle = '#fff'; g.fill(); g.strokeStyle = col; g.lineWidth = 1.5; g.stroke();
    }
    if (d?.kind === 'autoPt' && d.row.id === r.id) { const txt = fmtAuto(r.param, d.v, r.t); g.font = '600 11px "IBM Plex Mono"'; const tw = g.measureText(txt).width; const x = xOf(d.t) + 8, y = autoY(r, r.param, d.v) - 8; roundRect(g, x, y - 12, tw + 10, 17, 4); g.fillStyle = '#071026'; g.fill(); g.fillStyle = '#fff'; g.fillText(txt, x + 5, y); }
  }
  const hitAutoPoint = (r: Extract<Row, { kind: 'auto' }>, x: number, y: number) => A.points(r.t, r.param).findIndex((p) => Math.hypot(xOf(p.t) - x, autoY(r, r.param, p.v) - y) < 7);

  function drawOne(g: CanvasRenderingContext2D, c: Clip, row: Row | undefined, W: number, p: Project, on: boolean, alpha: number) {
    if (!row) return;
    const x = xOf(c.start), w = c.duration * zoom, y = yRow(row) + 2, h = row.h - 5;
    if (x > W || x + w < 0 || y > (wrap.current?.clientHeight ?? 0) || y + h < TOP) return;
    g.globalAlpha = alpha * (c.muted ? 0.7 : 1);
    drawClip(g, c, row.t, x, y, w, h, on, p, W, zoom);
    g.globalAlpha = 1;
  }

  // ---- Vorschau während Trim und Fade ----
  const trimPreview = (c: Clip, d: Extract<Drag, { kind: 'trim' }>): Clip => {
    const lim = op.trimLimits(c, (id) => engine.buffers.get(id)?.duration, useStudio.getState().project), dt = d.dx / zoom;
    if (d.edge === 'end') {
      let e = snapT(op.clipEnd(c) + dt);
      e = Math.max(c.start + 0.01, d.loopTrim || c.loop ? e : Math.min(lim.maxEnd, e));
      return { ...c, duration: e - c.start, loop: d.loopTrim && !c.loop ? { length: c.duration } : c.loop };
    }
    let s = snapT(c.start + dt);
    s = Math.min(op.clipEnd(c) - 0.01, Math.max(c.kind === 'audio' ? lim.minStart : 0, s));
    const q = { ...c, notes: c.notes, loop: c.loop };
    op.setBounds({ ...useStudio.getState().project, clips: [q] } as Project, q, s, op.clipEnd(c));
    return q;
  };
  const fadePreview = (c: Clip, d: Extract<Drag, { kind: 'fade' }>): Clip => {
    const dt = d.dx / zoom;
    if (d.edge === 'in') return { ...c, fadeIn: Math.max(0, Math.min(c.duration - c.fadeOut, c.fadeIn + dt)), fadeInShape: c.fadeInShape ?? ed.fades.inShape };
    return { ...c, fadeOut: Math.max(0, Math.min(c.duration - c.fadeIn, c.fadeOut - dt)), fadeOutShape: c.fadeOutShape ?? ed.fades.outShape };
  };

  // ---- Treffer ----
  const local = (e: { clientX: number; clientY: number }) => { const r = wrap.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  type Zone = 'trimStart' | 'trimEnd' | 'fadeIn' | 'fadeOut' | 'select' | 'grab';
  /** Warp-Ansicht: Warp-Marker unter der Maus (±5 px) */
  const hitWarp = (x: number, y: number): { clip: Clip; idx: number } | null => {
    const row = rowsRef.current[rowAt(y)], p = useStudio.getState().project;
    if (row?.kind !== 'track' || !useEdit.getState().warp.includes(row.id)) return null;
    for (const c of p.clips) {
      if (c.trackId !== row.id || !c.warp || xOf(c.start) > x + 6 || xOf(c.start + c.duration) < x - 6) continue;
      const b0 = beatAt(p, c.start);
      const idx = c.warp.points.findIndex((w) => { const t = secAt(p, b0 + w.b); return t > c.start + 1e-4 && t < c.start + c.duration - 1e-4 && Math.abs(xOf(t) - x) <= 5; });
      if (idx >= 0) return { clip: c, idx };
    }
    return null;
  };
  const hitClip = (x: number, y: number): { clip: Clip; zone: Zone; row: Row } | null => {
    const ri = rowAt(y), row = rowsRef.current[ri];
    if (!row || row.kind !== 'track') return null;
    const p = useStudio.getState().project;
    const clips = p.clips.filter((c) => c.trackId === row.id);
    for (let k = clips.length - 1; k >= 0; k--) {
      const c = clips[k], cx = xOf(c.start), cw = c.duration * zoom, cy = yRow(row) + 2, ch = row.h - 5;
      if (x < cx - 1 || x > cx + cw + 1 || y < cy || y > cy + ch) continue;
      const edgeW = Math.min(9, cw / 4), nearL = x < cx + edgeW, nearR = x > cx + cw - edgeW, upper = y < cy + ch / 2, corner = y < cy + Math.min(16, ch / 3);
      const tool = useEdit.getState().tool;
      let zone: Zone;
      if (tool === 'trim') zone = x < cx + cw / 2 ? 'trimStart' : 'trimEnd';
      else if (tool === 'select') zone = 'select';
      else if (tool === 'grab' || tool === 'pencil' || tool === 'scrub' || tool === 'cut') zone = 'grab';
      else if (c.kind === 'audio' && corner && (nearL || x < cx + c.fadeIn * zoom + 5) && x < cx + cw / 2) zone = 'fadeIn';
      else if (c.kind === 'audio' && corner && (nearR || x > cx + cw - c.fadeOut * zoom - 5) && x > cx + cw / 2) zone = 'fadeOut';
      else if (nearL) zone = 'trimStart';
      else if (nearR) zone = 'trimEnd';
      else zone = upper ? 'select' : 'grab';
      return { clip: c, zone, row };
    }
    return null;
  };
  const markerAt = (x: number, y: number) => {
    const MK = RL.get('markers')!;
    if (y < MK.y || y > MK.y + MK.h) return null;
    const g = canvas.current?.getContext('2d');
    if (g) g.font = '600 10.5px "IBM Plex Sans"';
    return [...useStudio.getState().project.markers].sort((a, b) => b.time - a.time).find((m) => { const mx = xOf(m.time), tw = (g?.measureText(m.name).width ?? 40) + 22; return x >= mx - 2 && x <= mx + tw; }) ?? null;
  };

  const setRange = (r: op.Range | null, locate = true) => {
    useEdit.getState().setRange(r);
    if (r && locate && !useStudio.getState().transport.playing) useStudio.getState().setTransport({ position: r.start });
    if (r) updatePresence({ cursor: { time: r.start, trackId: r.trackIds[0] } });
  };

  // ---- Maus ----
  const onDown = (e: React.PointerEvent) => {
    if (e.button === 2) return;
    setCtxMenu(null);
    const { x, y } = local(e), p = useStudio.getState().project;
    (e.target as Element).setPointerCapture(e.pointerId);
    if (y < RULER) {
      // Tempo-Lineal: Klick öffnet Tempo und Taktart an dieser Stelle
      const TE = RL.get('tempo');
      if (TE && y >= TE.y && y < TE.y + TE.h) { useEdit.getState().open('tempo', String(Math.max(0, op.snapGrid(p, secOf(x), '1/4')))); return; }
      const m = markerAt(x, y);
      if (m) { drag.current = { kind: 'marker', id: m.id, t0: m.time, x0: e.clientX, dx: 0 }; return; }
      const t = snapT(secOf(x), e);
      drag.current = { kind: 'ruler', t0: t, t1: t, x0: e.clientX, moved: false };
      return;
    }
    if (y < TOP) { engine.locate(Math.max(0, secOf(x))); return; }
    const ri = rowAt(y), row = rowsRef.current[ri];
    const tool = useEdit.getState().tool;
    if (row?.kind === 'auto') {
      const idx = hitAutoPoint(row, x, y);
      if (idx >= 0 && e.altKey) { commit('Automationspunkt löschen', (q) => { const t = A.trackLike(q, row.t.id); if (t) A.deletePoint(t, row.param, idx); }); return; }
      if (idx >= 0) { const pt = A.points(row.t, row.param)[idx]; drag.current = { kind: 'autoPt', row, idx, t: pt.t, v: pt.v, moved: false }; return; }
      if (tool === 'pencil') { drag.current = { kind: 'autoDraw', row, pts: [{ t: Math.max(0, secOf(x)), v: autoV(row, row.param, y) }] }; return; }
      drag.current = { kind: 'autoClick', row, t0: snapT(secOf(x), e), x0: e.clientX, y, moved: false };
      return;
    }
    if (tool === 'scrub') {
      const ids = row ? [row.t.id] : trackRows().map((r) => r.id);
      drag.current = { kind: 'scrub', trackIds: e.shiftKey ? trackRows().map((r) => r.id) : ids };
      void engine.scrub(drag.current.trackIds, Math.max(0, secOf(x)));
      return;
    }
    if (tool === 'pencil' && row?.kind === 'track' && row.t.kind === 'midi' && !hitClip(x, y)) {
      const t = snapT(secOf(x), e); drag.current = { kind: 'pencil', trackId: row.id, t0: t, t1: t }; return;
    }
    if (tool === 'cut') {
      // Schere: Klick schneidet den Clip an dieser Stelle (mit Raster), ⌥-Klick auf allen Spuren, Edit-Gruppen schneiden mit
      if (row?.kind !== 'track') return;
      const t = snapT(secOf(x), e), ids = e.altKey ? trackRows().map((r) => r.id) : expandEdit(p, [row.id]);
      const before = p.clips.length;
      commit('Schneiden', (q) => op.splitTracksAt(q, ids, t));
      const n = useStudio.getState().project.clips.length - before;
      if (!n) toast('Hier liegt kein Clip zum Schneiden');
      setRange({ start: t, end: t, trackIds: ids }, false);
      return;
    }
    const hw = tool !== 'pencil' ? hitWarp(x, y) : null;
    if (hw) {
      // Warp-Marker: ziehen dehnt das Audio links und rechts davon, ⌥-Klick löscht ihn
      if (e.altKey) { commit('Warp-Marker löschen', (q) => { const c = q.clips.find((z) => z.id === hw.clip.id); if (c) removeWarp(c, hw.idx); }); return; }
      const t = secAt(p, beatAt(p, hw.clip.start) + hw.clip.warp!.points[hw.idx].b);
      select([hw.clip.id], hw.clip.trackId);
      drag.current = { kind: 'warp', clipId: hw.clip.id, idx: hw.idx, t, moved: false };
      return;
    }
    const h = hitClip(x, y);
    if (h) {
      const c = h.clip;
      if (h.zone === 'trimStart' || h.zone === 'trimEnd') { select([c.id], c.trackId); drag.current = { kind: 'trim', id: c.id, edge: h.zone === 'trimStart' ? 'start' : 'end', x0: e.clientX, dx: 0, loopTrim: e.altKey && h.zone === 'trimEnd' }; return; }
      if (h.zone === 'fadeIn' || h.zone === 'fadeOut') { select([c.id], c.trackId); drag.current = { kind: 'fade', id: c.id, edge: h.zone === 'fadeIn' ? 'in' : 'out', x0: e.clientX, dx: 0 }; return; }
      if (h.zone === 'grab') {
        const mod = e.metaKey || e.ctrlKey;
        if (mod) suppressCtx.current = true;
        if (mod && sel.clipIds.includes(c.id)) {
          // ⌘/⌃-Klick auf einen ausgewählten Clip nimmt ihn (mit seiner Gruppe) wieder heraus
          const drop = op.withGroups(p, [c.id]);
          const rest = sel.clipIds.filter((x) => !drop.includes(x));
          select(rest, rest.length ? p.clips.find((x) => x.id === rest[0])!.trackId : c.trackId);
          const cl = p.clips.filter((x) => rest.includes(x.id));
          setRange(cl.length ? { start: Math.min(...cl.map((x) => x.start)), end: Math.max(...cl.map(op.clipEnd)), trackIds: [...new Set(cl.map((x) => x.trackId))] } : null, false);
          return;
        }
        const ids = op.withGroups(p, editGroupClips(p, e.shiftKey || mod ? [...new Set([...sel.clipIds, c.id])] : sel.clipIds.includes(c.id) ? sel.clipIds : [c.id]));
        select(ids, c.trackId);
        const clips = p.clips.filter((x) => ids.includes(x.id));
        setRange({ start: Math.min(...clips.map((x) => x.start)), end: Math.max(...clips.map(op.clipEnd)), trackIds: [...new Set(clips.map((x) => x.trackId))] }, false);
        drag.current = { kind: 'move', ids, anchor: c.id, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, copy: e.altKey, spot: useEdit.getState().mode === 'spot' };
        return;
      }
    }
    if (!row) { select([], null); setRange(null); return; }
    // Selector: Einfügemarke oder Bereich, auch über mehrere Spuren
    const t = snapT(secOf(x), e);
    const cur = useEdit.getState().range;
    if (e.shiftKey && cur) {
      const ids = row.kind === 'track' && cur.trackIds.every((id) => !id.startsWith('lane:')) ? [...new Set([...cur.trackIds, row.id])] : cur.trackIds;
      setRange({ start: Math.min(cur.start, t), end: Math.max(cur.end, t), trackIds: ids }, false);
      return;
    }
    if ((e.metaKey || e.ctrlKey) && row.kind === 'track' && cur) {
      // ⌘-Klick (oder ⌃-Klick am Mac) nimmt die Spur zur Auswahl dazu bzw. heraus, Zeitbereich bleibt
      suppressCtx.current = true;
      const tracksNow = cur.trackIds.filter((id) => !id.includes(':'));
      drag.current = { kind: 'select', row, t0: t, t1: t, rowIdx0: ri, rowIdx1: ri, x0: e.clientX, moved: false, add: tracksNow, toggle: true, keep: { start: cur.start, end: cur.end } };
      return;
    }
    select([], row.kind === 'track' ? row.id : row.t?.id ?? null);
    drag.current = { kind: 'select', row, t0: t, t1: t, rowIdx0: ri, rowIdx1: ri, x0: e.clientX, moved: false };
    setRange({ start: t, end: t, trackIds: row.kind === 'folder' ? row.kids.map((k) => k.id) : [row.id] });
  };

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current, { x, y } = local(e);
    editCursor.t = Math.max(0, secOf(x)); editCursor.y = y;
    if (!d) {
      const ar = y > TOP ? rowsRef.current[rowAt(y)] : undefined;
      if (ar?.kind === 'auto') { wrap.current!.style.cursor = hitAutoPoint(ar, x, y) >= 0 ? (e.altKey ? 'not-allowed' : 'move') : useEdit.getState().tool === 'pencil' ? 'crosshair' : 'copy'; return; }
      if (y > TOP && hitWarp(x, y)) { wrap.current!.style.cursor = e.altKey ? 'not-allowed' : 'ew-resize'; return; }
      const h = y > TOP ? hitClip(x, y) : null, m = markerAt(x, y);
      const cur: Record<Zone, string> = { trimStart: 'w-resize', trimEnd: 'e-resize', fadeIn: 'nw-resize', fadeOut: 'ne-resize', select: 'text', grab: 'grab' };
      const tl = useEdit.getState().tool;
      wrap.current!.style.cursor = y < RULER ? (m ? 'grab' : 'col-resize') : y < TOP ? 'pointer' : tl === 'scrub' ? 'ew-resize' : tl === 'cut' ? (h ? 'crosshair' : 'default') : h ? cur[h.zone] : tl === 'pencil' ? 'crosshair' : 'text';
      return;
    }
    if (d.kind === 'scrub') { void engine.scrub(d.trackIds, Math.max(0, secOf(x))); return; }
    if (d.kind === 'warp') { d.t = Math.max(0, snapT(secOf(x), e)); d.moved = true; force((n) => n + 1); return; }
    if (d.kind === 'autoPt') { d.t = Math.max(0, snapT(secOf(x), e)); d.v = autoV(d.row, d.row.param, y); d.moved = true; force((n) => n + 1); return; }
    if (d.kind === 'autoDraw') { const t = Math.max(0, secOf(x)), last = d.pts[d.pts.length - 1]; if (Math.abs(t - last.t) > 0.03) d.pts.push({ t, v: autoV(d.row, d.row.param, y) }); force((n) => n + 1); return; }
    if (d.kind === 'autoClick') {
      if (!d.moved && Math.abs(e.clientX - d.x0) < 4) return;
      d.moved = true;
      const t = snapT(secOf(x), e);
      setRange({ start: Math.min(d.t0, t), end: Math.max(d.t0, t), trackIds: [d.row.id] }, false);
      return;
    }
    if (d.kind === 'select') {
      const t = snapT(secOf(x), e), ri = rowAt(y);
      d.t1 = t; d.moved = d.moved || Math.abs(e.clientX - d.x0) > 3;
      if (ri >= 0) d.rowIdx1 = ri;
      const rs = rowsRef.current;
      const between = d.row.kind === 'lane' ? [d.row.id] : rs.slice(Math.min(d.rowIdx0, d.rowIdx1), Math.max(d.rowIdx0, d.rowIdx1) + 1).flatMap((r) => r.kind === 'track' ? [r.id] : r.kind === 'folder' ? r.kids.map((k) => k.id) : []);
      const ids = orderTracks([...new Set([...(d.add ?? []), ...between])]);
      setRange({ start: Math.min(d.t0, t), end: Math.max(d.t0, t), trackIds: ids.length ? ids : [d.row.id] }, false);
      return;
    }
    if (d.kind === 'ruler') { d.t1 = snapT(secOf(x), e); d.moved = d.moved || Math.abs(e.clientX - d.x0) > 3; force((n) => n + 1); return; }
    if (d.kind === 'pencil') { d.t1 = snapT(secOf(x), e); force((n) => n + 1); return; }
    if (d.kind === 'move') { d.dx = e.clientX - d.x0; d.dy = e.clientY - d.y0; d.copy = e.altKey; }
    else if (d.kind === 'trim' || d.kind === 'fade' || d.kind === 'marker') d.dx = e.clientX - d.x0;
    force((n) => n + 1);
  };

  const onUp = (e: React.PointerEvent) => {
    const d = drag.current; drag.current = null;
    if (!d) return;
    const p = useStudio.getState().project, E = useEdit.getState();
    if (d.kind === 'warp') {
      if (d.moved) commit('Warp-Marker verschieben', (q) => { const c = q.clips.find((z) => z.id === d.clipId); if (c) moveWarp(q, c, d.idx, d.t); });
      return;
    }
    if (d.kind === 'select') {
      if (d.toggle && !d.moved) {
        const has = d.add!.includes(d.row.id);
        const ids = orderTracks(has ? d.add!.filter((x) => x !== d.row.id) : [...d.add!, d.row.id]);
        setRange(ids.length ? { ...d.keep!, trackIds: ids } : null, false);
        select([], ids[0] ?? null);
        return;
      }
      if (!d.moved) engine.locate(d.t0);
      return;
    }
    if (d.kind === 'scrub') return;
    if (d.kind === 'autoPt') {
      if (d.moved) commit('Automationspunkt', (q) => { const t = A.trackLike(q, d.row.t.id); if (t) A.movePoint(t, d.row.param, d.idx, d.t, d.v); });
      return;
    }
    if (d.kind === 'autoClick') {
      if (!d.moved) commit('Automationspunkt', (q) => { const t = A.trackLike(q, d.row.t.id); if (t) A.addPoint(t, d.row.param, d.t0, autoV(d.row, d.row.param, d.y)); });
      return;
    }
    if (d.kind === 'autoDraw') {
      if (d.pts.length < 2) return;
      const pts = [...d.pts].sort((a, b) => a.t - b.t);
      commit('Automation zeichnen', (q) => {
        const t = A.trackLike(q, d.row.t.id);
        if (!t) return;
        A.removeRange(t, d.row.param, pts[0].t, pts[pts.length - 1].t, false);
        for (const p of A.thin(pts, d.row.param === 'pan' ? 0.01 : 0.2)) A.addPoint(t, d.row.param, p.t, p.v);
      });
      return;
    }
    if (d.kind === 'ruler') {
      if (!d.moved) { engine.locate(Math.max(0, d.t0)); return; }
      const a = Math.min(d.t0, d.t1), b = Math.max(d.t0, d.t1);
      setRange({ start: a, end: b, trackIds: trackRows().map((r) => r.id) });
      if (p.loop.enabled) commit('Loop auf Auswahl', (q) => { q.loop.start = a; q.loop.end = b; });
      return;
    }
    if (d.kind === 'marker') {
      if (Math.abs(d.dx) < 3) {
        const m = p.markers.find((x) => x.id === d.id);
        if (m) { engine.locate(m.time); if (m.end !== undefined) setRange({ start: m.time, end: m.end, trackIds: trackRows().map((r) => r.id) }); }
        return;
      }
      const nt = snapT(d.t0 + d.dx / zoom);
      commit('Marker verschieben', (q) => { const m = q.markers.find((x) => x.id === d.id); if (m) { if (m.end !== undefined) m.end += nt - m.time; m.time = nt; } q.markers.sort((a, b) => a.time - b.time); });
      return;
    }
    if (d.kind === 'pencil') {
      const a = Math.min(d.t0, d.t1), b = Math.max(d.t0, d.t1), len = b - a < 0.05 ? secOfBar(p, barAtSec(p, a) + 1) - a : b - a;
      const id = uid('c'), t = p.tracks.find((x) => x.id === d.trackId)!;
      commit('MIDI-Clip zeichnen', (q) => q.clips.push({ id, trackId: d.trackId, kind: 'midi', name: t.name, start: a, offset: 0, duration: len, notes: [], fadeIn: 0, fadeOut: 0, by: { userId: user.id, at: new Date().toISOString() } }));
      select([id], d.trackId);
      return;
    }
    if (d.kind === 'move') {
      const anchor = p.clips.find((x) => x.id === d.anchor);
      if (!anchor) return;
      if (d.spot && Math.abs(d.dx) < 3 && Math.abs(d.dy) < 3) { E.open('spot', anchor.id); return; }
      if (Math.abs(d.dx) < 3 && Math.abs(d.dy) < 3) return;
      const locked = p.clips.filter((c) => d.ids.includes(c.id) && c.locked);
      if (locked.length && !d.copy) toast('Gesperrte Clips (Time Lock) bleiben stehen');
      const so = syncAnchor(p, anchor), dt = snapT(anchor.start + so + d.dx / zoom) - (anchor.start + so);
      const trs = trackRows(), dRows = Math.round(d.dy / ed.laneH);
      let newIds: string[] = [];
      commit(d.copy ? 'Clips kopieren' : 'Clips verschieben', (q) => {
        for (const id of d.ids) {
          const c = q.clips.find((x) => x.id === id);
          if (!c || (c.locked && !d.copy)) continue;
          const ti = trs.findIndex((r) => r.id === c.trackId), target = trs[Math.max(0, Math.min(trs.length - 1, ti + dRows))];
          const tid = target && (target.t.kind === 'midi') === (c.kind === 'midi') ? target.id : c.trackId;
          if (d.copy) { const n = { ...structuredClone(c), id: uid('c'), start: Math.max(0, c.start + dt), trackId: tid, locked: false }; q.clips.push(n); newIds.push(n.id); continue; }
          if (E.mode === 'shuffle') op.shuffleMove(q, c.id, tid, Math.max(0, c.start + dt));
          else { c.start = Math.max(0, c.start + dt); c.trackId = tid; q.clips.splice(q.clips.indexOf(c), 1); q.clips.push(c); }
          newIds.push(c.id);
        }
      });
      const moved = useStudio.getState().project.clips.filter((c) => newIds.includes(c.id));
      if (moved.length) { select(newIds, moved[0].trackId); setRange({ start: Math.min(...moved.map((c) => c.start)), end: Math.max(...moved.map(op.clipEnd)), trackIds: [...new Set(moved.map((c) => c.trackId))] }, false); }
      newIds = [];
      return;
    }
    if (d.kind === 'trim') {
      if (Math.abs(d.dx) < 2) return;
      const c = p.clips.find((x) => x.id === d.id);
      if (!c) return;
      const q = trimPreview(c, d);
      commit(d.loopTrim ? 'Loop-Trim' : 'Trimmen', (pr) => {
        const x = pr.clips.find((y) => y.id === d.id);
        if (!x) return;
        if (E.mode === 'shuffle') op.shuffleTrim(pr, x.id, d.edge, d.edge === 'end' ? op.clipEnd(q) - op.clipEnd(x) : q.start - x.start);
        else Object.assign(x, { start: q.start, duration: q.duration, offset: q.offset, notes: q.notes, loop: q.loop, fadeIn: q.fadeIn, fadeOut: q.fadeOut });
      });
      return;
    }
    if (d.kind === 'fade') {
      const c = p.clips.find((x) => x.id === d.id);
      if (!c || Math.abs(d.dx) < 2) return;
      const q = fadePreview(c, d);
      commit(d.edge === 'in' ? 'Fade-In' : 'Fade-Out', (pr) => { const x = pr.clips.find((y) => y.id === d.id); if (x) Object.assign(x, { fadeIn: q.fadeIn, fadeOut: q.fadeOut, fadeInShape: q.fadeInShape, fadeOutShape: q.fadeOutShape }); });
    }
    void e;
  };

  const onWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      const { x } = local(e), t = secOf(x), z = Math.max(8, Math.min(400, zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
      useStudio.getState().setZoom(z);
      setScroll((s) => ({ ...s, x: Math.max(0, t * z - x) }));
      return;
    }
    const H = wrap.current?.clientHeight ?? 0;
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) setScroll((s) => ({ ...s, x: Math.max(0, Math.min(projectLength(project) * zoom, s.x + (e.deltaX || e.deltaY))) }));
    else setScroll((s) => ({ ...s, y: Math.max(0, Math.min(Math.max(0, TOP + contentH - H + 60), s.y + e.deltaY)) }));
  };

  // Doppelklick: MIDI-Clip öffnet den Editor, Audio-Clip wählt ihn aus, leere MIDI-Spur legt einen Clip an,
  // unter den Spuren entsteht eine neue MIDI-Spur. Im Lineal: Abschnitt zwischen zwei Markern auswählen.
  const onDouble = (e: React.MouseEvent) => {
    const { x, y } = local(e), p = useStudio.getState().project;
    if (y < RULER) {
      const TE = RL.get('tempo');
      if (TE && y >= TE.y && y < TE.y + TE.h) return;
      const m = markerAt(x, y);
      if (m) { useEdit.getState().open('markers', m.id); return; }
      const t = secOf(x), ms = [...p.markers].sort((a, b) => a.time - b.time);
      const prev = [...ms].reverse().find((m2) => m2.time <= t), next = ms.find((m2) => m2.time > t);
      const a = prev?.time ?? 0, b = prev?.end !== undefined && prev.end > t ? prev.end : next?.time ?? Math.max(...p.clips.map(op.clipEnd), t + 1);
      setRange({ start: a, end: b, trackIds: trackRows().map((r) => r.id) });
      toast(`${prev?.name ?? 'Anfang'} ausgewählt, auf allen Spuren. Jetzt z. B. ⌘D zum Duplizieren oder ⌘X/⌘V zum Umstellen.`);
      return;
    }
    if (y < TOP) return;
    const ar = rowsRef.current[rowAt(y)];
    if (ar?.kind === 'auto') {
      const idx = hitAutoPoint(ar, x, y);
      if (idx >= 0) commit('Automationspunkt löschen', (q) => { const t = A.trackLike(q, ar.t.id); if (t) A.deletePoint(t, ar.param, idx); });
      return;
    }
    const h = hitClip(x, y);
    if (h && h.clip.kind === 'audio' && useEdit.getState().warp.includes(h.clip.trackId) && h.row.t.elastic) {
      // Warp-Ansicht: Doppelklick setzt einen Warp-Marker, rastet auf einen Event-Marker in der Nähe ein
      let t = secOf(x);
      const ev = h.clip.sourceId ? eventsOf(h.clip.sourceId) : null, m = clipMap(p, h.clip);
      const near = (ev ?? []).map((s) => m.toTime(s)).filter((tt) => Math.abs(xOf(tt) - x) <= 8).sort((a, b) => Math.abs(a - t) - Math.abs(b - t))[0];
      if (near !== undefined) t = near;
      let ok = false;
      commit('Warp-Marker setzen', (q) => { const c = q.clips.find((z) => z.id === h.clip.id); if (c) ok = addWarp(q, c, t); });
      if (!ok) toast('Hier liegt schon ein Warp-Marker');
      return;
    }
    if (h) {
      if (h.clip.kind === 'midi') onEdit(h.clip.id);
      else { select([h.clip.id], h.clip.trackId); setRange({ start: h.clip.start, end: op.clipEnd(h.clip), trackIds: [h.clip.trackId] }); onEdit(h.clip.id); }
      return;
    }
    const row = rowsRef.current[rowAt(y)];
    if (row && (row.kind !== 'track' || row.t.kind !== 'midi')) return;
    const b0 = Math.max(0, Math.floor(barAtSec(p, Math.max(0, secOf(x))))), start = secOfBar(p, b0), dur = secOfBar(p, b0 + 4) - start;
    const id = uid('c'), at = new Date().toISOString();
    commit(row ? 'MIDI-Clip anlegen' : 'MIDI-Spur mit Clip', (q) => {
      let track = row ? q.tracks.find((t) => t.id === row.id)! : undefined;
      if (!track) { track = emptyTrack(`MIDI ${q.tracks.length + 1}`, 'midi', COLORS[q.tracks.length % COLORS.length], { instrument: { plugin: 'srs:synth', preset: 'keys' } }); q.tracks.push(track); }
      q.clips.push({ id, trackId: track.id, kind: 'midi', name: track.name, start, offset: 0, duration: dur, notes: [], fadeIn: 0, fadeOut: 0, by: { userId: user.id, at } });
    });
    select([id], useStudio.getState().project.clips.find((c) => c.id === id)?.trackId ?? null);
    onEdit(id);
  };

  const onContext = (e: React.MouseEvent) => {
    e.preventDefault();
    // ⌃-Klick am Mac löst auch ein Kontextmenü aus: wenn er schon zum Auswählen benutzt wurde, kein Menü
    if (suppressCtx.current) { suppressCtx.current = false; return; }
    const { x, y } = local(e), h = y > TOP ? hitClip(x, y) : null;
    if (h) {
      if (!sel.clipIds.includes(h.clip.id)) { select([h.clip.id], h.clip.trackId); setRange({ start: h.clip.start, end: op.clipEnd(h.clip), trackIds: [h.clip.trackId] }, false); }
      setCtxMenu({ x: e.clientX, y: e.clientY, clipId: h.clip.id });
    } else if (useEdit.getState().range) setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  // ---- Comping ----
  function compSelection() {
    const r = useEdit.getState().range, p = useStudio.getState().project;
    const lid = r?.trackIds.find((id) => id.startsWith('lane:'));
    const row = rowsRef.current.find((x) => x.id === lid);
    if (!r || !row || row.kind !== 'lane') return toast('Erst in einer Take- oder Playlist-Lane einen Bereich auswählen');
    const range = op.isEmptyRange(r) ? { start: row.lane.clips[0]?.start ?? 0, end: Math.max(...row.lane.clips.map(op.clipEnd)) } : r;
    commit('In Haupt-Playlist kopieren', (q) => {
      const t = q.tracks.find((x) => x.id === row.t.id)!;
      if (row.lane.takeId) { const tk = q.takes.find((x) => x.id === row.lane.takeId); if (tk) op.compTake(q, t, tk, range); }
      else if (row.lane.playlistId) op.compPlaylist(q, t.id, row.lane.playlistId, range);
    });
    toast(`${row.lane.label}: ${fmtBar(p, range.start)} bis ${fmtBar(p, range.end)} in die Haupt-Playlist übernommen`);
  }

  // ---- Dateien importieren ----
  const hasFiles = (e: React.DragEvent) => [...e.dataTransfer.types].includes('Files');
  const onDragOver = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault(); e.dataTransfer.dropEffect = 'copy';
    const types = [...e.dataTransfer.items].map((i) => i.type);
    const kind = types.length && types.every((t) => /midi/.test(t)) ? 'midi' : types.length && types.every((t) => t.startsWith('audio/') && !/midi/.test(t)) ? 'audio' : 'mixed';
    dropAt.current = { ...local(e), kind };
    if (!dropping) setDropping(true);
  };
  const onDragLeave = (e: React.DragEvent) => { if (e.currentTarget.contains(e.relatedTarget as Node | null)) return; dropAt.current = null; setDropping(false); };
  const onDrop = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    const { x, y } = local(e);
    dropAt.current = null; setDropping(false);
    const row = y >= TOP ? rowsRef.current[rowAt(y)] : undefined;
    void importFiles([...e.dataTransfer.files], { time: x < 0 ? engine.position : Math.max(0, snapT(secOf(x))), trackId: row?.kind === 'track' ? row.id : null });
  };
  const pickFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const p = useStudio.getState().project;
    void importFiles([...files], { time: engine.position, trackId: sel.trackId ?? p.tracks[p.tracks.length - 1]?.id ?? null });
    if (fileInput.current) fileInput.current.value = '';
  };
  const V = ed.view, headW = 214 + (V.inserts ? 86 : 0);
  const hiddenN = project.tracks.filter((t) => t.hidden && t.kind !== 'bus').length;
  const addTrack = (kind: 'audio' | 'midi') => commit(kind === 'audio' ? 'Audiospur' : 'MIDI-Spur', (p) => p.tracks.push(kind === 'audio'
    ? emptyTrack(`Audio ${p.tracks.length + 1}`, 'audio', COLORS[p.tracks.length % COLORS.length], { input: { kind: 'browser' } })
    : emptyTrack(`MIDI ${p.tracks.length + 1}`, 'midi', COLORS[p.tracks.length % COLORS.length], { instrument: { plugin: 'srs:synth', preset: 'keys' } })));

  return (
    <section className={`panel editwin ${dropping ? 'dropping' : ''}`} style={{ minHeight: 0 }} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <EditToolbar />
      <div className="ed" style={{ flex: 1, minHeight: 0, gridTemplateColumns: `${V.tracks ? '172px ' : ''}${headW}px 1fr`, gridTemplateRows: `${TOP}px 1fr` }}>
        {V.tracks && <TrackList />}
        <div className="rlabels" style={{ gridColumn: V.tracks ? 2 : 1 }}>
          {RL.rows.map((r, i) => (
            <div key={r.id} className={`rl ${r.id}`} style={{ height: r.h }}>
              <span>{r.label}</span>
              {r.id === 'markers' && <button className="lnk" title="Marker setzen (M); Doppelklick auf einen Abschnitt wählt ihn auf allen Spuren" onClick={() => run('marker')}>+</button>}
              {r.id === 'chords' && <span className="pill blue">{project.key}</span>}
              {r.id === 'tempo' && <button className="lnk" title="Tempo- oder Taktwechsel setzen (⌥⇧T); Klick ins Tempo-Lineal setzt ihn an dieser Stelle" onClick={() => useEdit.getState().open('tempo', String(engine.position))}>+</button>}
              {i === RL.rows.length - 1 && V.inserts && <b className="inshdr">Inserts A–E</b>}
            </div>
          ))}
        </div>
        <div className="canvaswrap" ref={wrap} style={{ gridColumn: V.tracks ? 3 : 2 }} onPointerDown={onDown} onPointerMove={onMove} onPointerLeave={() => { editCursor.t = null; editCursor.y = null; }} onPointerUp={onUp} onWheel={onWheel} onDoubleClick={onDouble} onContextMenu={onContext}>
          <canvas ref={canvas} />
        </div>
        <div className="heads" style={{ gridColumn: V.tracks ? 2 : 1 }} onScroll={(e) => { e.currentTarget.scrollTop = 0; }}>
          <div style={{ transform: `translateY(${-scroll.y}px)` }}>
            {rows.map((r) => r.kind === 'track'
              ? <TrackHead key={r.id} t={r.t} h={r.h} nested={r.nested} inserts={V.inserts} selected={sel.trackId === r.id || !!ed.range?.trackIds.includes(r.id)} lanesOpen={ed.lanes.includes(r.id)} />
              : r.kind === 'auto' ? <AutoHead key={r.id} t={r.t} param={r.param} h={r.h} />
              : r.kind === 'folder' ? <FolderHead key={r.id} f={r.f} kids={r.kids} h={r.h} selected={r.kids.length > 0 && r.kids.every((k) => ed.range?.trackIds.includes(k.id))} />
              : <LaneHead key={r.id} row={r} solo={laneSolo[r.t.id] === r.id} onSolo={() => setLaneSolo((s) => { const n = { ...s }; if (n[r.t.id] === r.id) delete n[r.t.id]; else n[r.t.id] = r.id; return n; })}
                onComp={() => { const cur = useEdit.getState().range; if (!cur || !cur.trackIds.includes(r.id)) useEdit.getState().setRange({ start: r.lane.clips[0]?.start ?? 0, end: Math.max(...r.lane.clips.map(op.clipEnd)), trackIds: [r.id] }); compSelection(); }} />)}
            <div className="addrow">
              <button className="btn small" onClick={() => addTrack('audio')}>+ Audio</button>
              <button className="btn small" onClick={() => addTrack('midi')}>+ MIDI</button>
              <button className="btn small" title="Ausgewählte Spuren in einen Ordner legen (⌥⇧F)" onClick={() => run('folder')}>+ Ordner</button>
              <button className="btn small" title="WAV, MP3, AIFF, FLAC oder MIDI. Geht auch per Drag & Drop in die Timeline." onClick={() => fileInput.current?.click()}>Importieren</button>
              <button className="btn small" title="Mixdown und Stems als WAV/ZIP (⌥⌘B)" onClick={() => run('export')}>Export</button>
              <button className={`btn small ${ed.autoLanes.master?.length ? 'on' : ''}`} title="Master-Automation: Lautstärke (z. B. Fade-out am Songende) und Parameter der Master-Plugins (⌥⇧M)" onClick={() => run('autoMaster')}>Master-Automation</button>
              <input ref={fileInput} type="file" multiple accept={ACCEPT} hidden onChange={(e) => pickFiles(e.target.files)} />
            </div>
            {hiddenN > 0 && <div className="t2 drophint">{hiddenN} {hiddenN === 1 ? 'Spur ist' : 'Spuren sind'} ausgeblendet. <button className="lnk" onClick={() => ed.setView({ tracks: true })}>Spurliste zeigen</button></div>}
            <div className="t2 drophint">Audio und MIDI hierher ziehen. Doppelklick auf MIDI öffnet den Editor. Rechtsklick auf einen Clip für alle Befehle, auf einen Spurkopf für Spur-Befehle (Löschen, Duplizieren, Farbe).</div>
          </div>
        </div>
      </div>
      {ctxMenu && <ContextMenu at={ctxMenu} onClose={() => setCtxMenu(null)} onEdit={onEdit} />}
      {trackMenu && <TrackMenu at={trackMenu} onClose={() => setTrackMenu(null)} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Toolbar: Edit-Modi, Werkzeuge, Raster, Nudge, Auswahl, Bearbeiten-Menü
function EditToolbar() {
  const ed = useEdit();
  const [menu, setMenu] = useState<null | 'edit' | 'view'>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const off = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(null); };
    window.addEventListener('pointerdown', off, true);
    return () => window.removeEventListener('pointerdown', off, true);
  }, [menu]);
  const modes: op.EditMode[] = ['shuffle', 'spot', 'slip', 'grid'];
  const fkey: Record<op.EditMode, string> = { shuffle: 'F1', slip: 'F2', spot: 'F3', grid: 'F4' };
  const tools: op.Tool[] = ['smart', 'trim', 'select', 'grab', 'cut', 'scrub', 'pencil'];
  const tkey: Record<op.Tool, string> = { smart: '⌥5', trim: 'F6', select: 'F7', grab: 'F8', scrub: 'F9', pencil: 'F10', cut: '⇧B' };
  const V = ed.view;
  const toggle = (k: keyof typeof V, label: string, hint: string) => (
    <button className={`vt ${V[k] ? 'on' : ''}`} title={hint} onClick={() => ed.setView({ [k]: !V[k] })}><i>{V[k] ? '✓' : ''}</i>{label}</button>
  );
  return (
    <div className="etb" ref={ref}>
      <div className="modes2" title="Edit-Modus (F1–F4)">
        {modes.map((m) => <button key={m} className={`${m} ${ed.mode === m ? 'on' : ''}`} title={`${MODE_LABEL[m]} (${fkey[m]})`} onClick={() => ed.set({ mode: m })}>{MODE_LABEL[m]}</button>)}
      </div>
      <div className="etg">
        <div className="seg small tools">
          {tools.map((t) => <button key={t} className={ed.tool === t ? 'on' : ''} title={`${TOOL_LABEL[t]} (${tkey[t]})`} onClick={() => ed.set({ tool: t })}><ToolIcon t={t} /></button>)}
        </div>
        <div className="row zr">
          <button className="ib" title="Herauszoomen (− oder ⌘[)" onClick={() => run('zoomOut')}>−</button>
          <button className="ib" title="Hineinzoomen (+ oder ⌘])" onClick={() => run('zoomIn')}>+</button>
          <button className="ib w" title="Auf Auswahl zoomen (E)" onClick={() => run('zoomSel')}>Auswahl</button>
          <button className="ib w all" title="Ganzer Song (⌥A)" onClick={() => run('zoomAll')}>Alles</button>
          <button className={`ib w ${ed.snap || ed.mode === 'grid' ? 'on' : ''}`} disabled={ed.mode === 'grid'} title="Snap to Grid (⇧F4)" onClick={() => ed.set({ snap: !ed.snap })}>Snap</button>
          <button className={`ib w ${ed.tabTransient ? 'on' : ''}`} title="Tab to Transient: Tab springt zum nächsten Anschlag, B schneidet dort" onClick={() => ed.set({ tabTransient: !ed.tabTransient })}>Tab→♩</button>
        </div>
      </div>
      <Counter />
      <div className="etg gn">
        <label className="etl" title="Raster für Grid und Snap"><span>Raster</span><select className="input" value={ed.grid} onChange={(e) => ed.set({ grid: e.target.value })}>{op.GRIDS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select></label>
        <label className="etl" title="Nudge-Wert für , und ."><span>Nudge</span><select className="input" value={ed.nudge} onChange={(e) => ed.set({ nudge: e.target.value })}>{op.NUDGES.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select></label>
      </div>
      <div className="etg quick">
        <div className="row">
          <button className="btn small" title="Strip Silence (⌘U)" onClick={() => run('strip')}>Strip Silence</button>
          <button className="btn small" title="Beat Detective (⌘8): Anschläge erkennen, schneiden und aufs Raster ziehen" onClick={() => run('beat')}>Beat Detective</button>
          <button className="btn small" title="Vocal-Timing (⌥⇧V): Silben aufs Raster oder auf das Lead-Vocal ziehen" onClick={() => run('vtiming')}>Vocal-Timing</button>
          <button className="btn small" title="Fades (⌘F)" onClick={() => run('fades')}>Fades</button>
        </div>
        <div className="row">
          <button className="btn small" title="Marker setzen (M)" onClick={() => run('marker')}>+ Marker</button>
          <button className={`btn small ${ed.follow ? 'on' : ''}`} title="Bildschirm folgt dem Abspielkopf" onClick={() => ed.set({ follow: !ed.follow })}>Mitlaufen</button>
        </div>
      </div>
      <div className="etr">
        <div className="seg small lh3" title="Spurhöhe">
          {[[50, 'S'], [73, 'M'], [110, 'L']].map(([h, l]) => <button key={l} className={ed.laneH === h ? 'on' : ''} title={`Spurhöhe ${l}`} onClick={() => ed.set({ laneH: h as number })}>{l}</button>)}
        </div>
        <div className="emenu">
          <button className={`btn small ${menu === 'view' ? 'on' : ''}`} onClick={() => setMenu(menu === 'view' ? null : 'view')}>Ansicht ▾</button>
          {menu === 'view' && (
            <div className="cmdlist viewmenu">
              <div className="cg"><small>Edit-Fenster</small>
                {toggle('tracks', 'Spurliste', 'Liste aller Spuren links: ein- und ausblenden, umsortieren')}
                {toggle('inserts', 'Inserts A–E', 'Insert-Spalte an den Spurköpfen')}
              </div>
              <div className="cg"><small>Lineale</small>
                {toggle('minsec', 'Min:Sek', 'Zeitlineal in Minuten und Sekunden')}
                {toggle('tempo', 'Tempo', 'Tempo und Taktart')}
                {toggle('chords', 'Akkorde', 'Akkordspur')}
              </div>
              <div className="cg"><small>Spurhöhe</small>
                <div className="seg small" style={{ margin: '2px 8px 4px' }}>{[[50, 'Klein'], [73, 'Mittel'], [110, 'Groß']].map(([h, l]) => <button key={l} className={ed.laneH === h ? 'on' : ''} onClick={() => ed.set({ laneH: h as number })}>{l}</button>)}</div>
              </div>
              <div className="cg"><small>Fenster</small>
                <button className={`vt ${V.side ? 'on' : ''}`} onClick={() => ed.setView({ side: V.side ? null : 'room' })}><i>{V.side ? '✓' : ''}</i>Seitenleiste (Raum, Clips, Marker)</button>
                <button className={`vt ${!V.dockClosed ? 'on' : ''}`} onClick={() => ed.setView({ dockClosed: !V.dockClosed })}><i>{!V.dockClosed ? '✓' : ''}</i>Dock unten (Takes, MIDI-Editor)</button>
              </div>
            </div>
          )}
        </div>
        <div className="emenu">
          <button className="btn small primary" onClick={() => setMenu(menu === 'edit' ? null : 'edit')}>Bearbeiten ▾</button>
          {menu === 'edit' && <CommandList onPick={() => setMenu(null)} />}
        </div>
      </div>
    </div>
  );
}

/** Zähler wie in Pro Tools: Position groß, darunter Min:Sek; Auswahl Anfang/Ende/Länge; Cursor unter der Maus */
function Counter() {
  const p = useStudio((s) => s.project);
  const r = useEdit((s) => s.range);
  const main = useRef<HTMLSpanElement>(null), sub = useRef<HTMLSpanElement>(null), cur = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const pr = useStudio.getState().project, pos = engine.position;
      if (main.current) main.current.textContent = fmtBBT(pr, pos).replace(/\./g, ' | ');
      if (sub.current) sub.current.textContent = fmtMinSec(pos);
      if (cur.current) cur.current.textContent = editCursor.t === null ? '–' : fmtBBT(pr, editCursor.t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div className="counter mono" title="Position (Takt | Schlag | Tick) und Auswahl">
      <div className="cmain"><span ref={main}>1 | 1 | 000</span><small><span ref={sub}>0:00,000</span></small></div>
      <div className="csel">
        <span><small>Anfang</small>{r ? fmtBBT(p, r.start) : '–'}</span>
        <span><small>Ende</small>{r ? fmtBBT(p, r.end) : '–'}</span>
        <span><small>Länge</small>{r ? fmtLen(p, r.end - r.start, r.start) : '–'}</span>
      </div>
      <div className="ccur"><small>Cursor</small><span ref={cur}>–</span></div>
    </div>
  );
}
export const fmtMinSec = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(3).padStart(6, '0').replace('.', ',')}`;

function ToolIcon({ t }: { t: op.Tool }) {
  const d: Record<op.Tool, string> = {
    smart: 'M3 2v8l2.5-2 2 4 1.5-.7-2-4H10z M11 9h3v5h-3z',
    trim: 'M4 2v12M4 8h8M9 5l3 3-3 3',
    select: 'M8 2v12M5 2h6M5 14h6',
    grab: 'M5 7V3.5a1 1 0 0 1 2 0V7m0-1V2.5a1 1 0 0 1 2 0V7m0-.5V3.5a1 1 0 0 1 2 0V9c0 3-1.5 5-4 5s-4-1.5-4.5-3L3 8.5a1 1 0 0 1 1.7-1L5 8',
    scrub: 'M3 6v4M6 4v8M9 6v4M12 7v2M1 8h1M14 8h1',
    pencil: 'M3 13l1-3 7-7 2 2-7 7z',
    cut: 'M3.2 4.2a1.7 1.7 0 1 0 3.4 0a1.7 1.7 0 1 0-3.4 0M3.2 11.8a1.7 1.7 0 1 0 3.4 0a1.7 1.7 0 1 0-3.4 0M6.3 5.3L14 11M6.3 10.7L14 5',
  };
  return <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round"><path d={d[t]} /></svg>;
}

function CommandList({ onPick, ids }: { onPick: () => void; ids?: string[] }) {
  const list = ids ? ids.map((id) => COMMANDS.find((c) => c.id === id)!).filter(Boolean) : COMMANDS.filter((c) => !['undo', 'redo'].includes(c.id) && !c.group.startsWith('Edit-Modus') && c.group !== 'Werkzeug');
  const groups = [...new Set(list.map((c) => c.group))];
  return (
    <div className="cmdlist">
      {groups.map((g) => (
        <div key={g}>
          {!ids && <small>{g}</small>}
          {list.filter((c) => c.group === g).map((c) => <button key={c.id} onClick={() => { onPick(); void c.run(); }}><span>{c.label}</span><kbd>{keyLabel(c.keys)}</kbd></button>)}
        </div>
      ))}
    </div>
  );
}

function ContextMenu({ at, onClose, onEdit }: { at: NonNullable<Ctx>; onClose: () => void; onEdit: (id: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(at.y);
  // Menü vollständig im Fenster halten
  useLayoutEffect(() => { const h = ref.current?.offsetHeight ?? 0; setTop(Math.max(8, Math.min(at.y, window.innerHeight - h - 8))); }, [at.y]);
  useEffect(() => {
    const off = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener('pointerdown', off, true); window.addEventListener('keydown', esc, true);
    return () => { window.removeEventListener('pointerdown', off, true); window.removeEventListener('keydown', esc, true); };
  }, [onClose]);
  const c = at.clipId ? useStudio.getState().project.clips.find((x) => x.id === at.clipId) : undefined;
  const ids = c
    ? [...(c.kind === 'midi' ? [] : ['fades', 'fadeIn', 'fadeOut']), 'separate', 'heal', 'trimSel', 'duplicate', 'repeat', 'copy', 'cut', 'clear', 'mute', 'lock', c.loop ? 'unloop' : 'loop', c.groupId ? 'ungroup' : 'group', 'gain', ...(c.kind === 'audio' ? ['vtiming', 'beatAll', 'syncSet', ...(c.syncPoint !== undefined ? ['syncClear'] : []), 'audiosuite', 'pitch', 'normalize', 'strip', 'beat', ...(c.warp ? ['equant'] : []), 'consolidate', ...(c.takeId ? ['deleteTake'] : [])] : ['events', 'consolidate']), 'quantizeClips', 'spot', 'rename']
    : ['cut', 'copy', 'paste', 'clear', 'duplicate', 'repeat', 'fill', 'separate', 'heal', 'trimSel', 'insertSilence', 'audiosuite', 'strip', 'beat', 'equant', 'fades', 'consolidate', 'marker', 'comp'];
  return (
    <div className="ctxmenu" ref={ref} style={{ left: Math.min(at.x, window.innerWidth - 300), top }}>
      {c && <div className="mh">{c.name}{c.muted ? ' · stumm' : ''}{c.locked ? ' · gesperrt' : ''}{c.loop ? ' · Loop' : ''}</div>}
      {c?.kind === 'midi' && <button className="first" onClick={() => { onClose(); onEdit(c.id); }}><span>Im MIDI-Editor öffnen</span><kbd>Doppelklick</kbd></button>}
      {c?.kind === 'audio' && <button className="first" onClick={() => { onClose(); onEdit(c.id); }}><span>Vocal-Tuning (Tonhöhe) öffnen</span><kbd>Doppelklick</kbd></button>}
      <CommandList ids={ids} onPick={onClose} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kontextmenü der Spur (Rechtsklick auf Spurkopf oder Spurliste), wirkt auf alle ausgewählten Spuren
/** Rechtsklick: ⌃-Klick (Mac, linke Taste) nimmt wie bisher eine Spur zur Auswahl dazu, sonst öffnet das Menü */
function openTrackMenu(id: string, e: React.MouseEvent) {
  if (e.ctrlKey && e.button === 0 && !e.altKey) { clickTrack(id, { metaKey: true, ctrlKey: true, shiftKey: false }); return; }
  const sel = selectedTrackIds();
  if (!sel.includes(id)) clickTrack(id, { metaKey: false, ctrlKey: false, shiftKey: false });
  window.dispatchEvent(new CustomEvent('srs:trackMenu', { detail: { id, x: e.clientX, y: e.clientY } }));
}
function TrackMenu({ at, onClose }: { at: { id: string; x: number; y: number }; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(at.y);
  const project = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  useLayoutEffect(() => { const h = ref.current?.offsetHeight ?? 0; setTop(Math.max(8, Math.min(at.y, window.innerHeight - h - 8))); }, [at.y]);
  useEffect(() => {
    const off = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener('pointerdown', off, true); window.addEventListener('keydown', esc, true);
    return () => { window.removeEventListener('pointerdown', off, true); window.removeEventListener('keydown', esc, true); };
  }, [onClose]);
  const t = project.tracks.find((x) => x.id === at.id);
  if (!t) return null;
  const ids = (() => { const s = selectedTrackIds(); return s.includes(t.id) ? s : [t.id]; })();
  const many = ids.length > 1;
  const sel = project.tracks.filter((x) => ids.includes(x.id));
  const act = (f: () => void) => () => { onClose(); f(); };
  const addBelow = (kind: 'audio' | 'midi') => act(() => {
    let nid = '';
    commit(kind === 'audio' ? 'Audiospur' : 'MIDI-Spur', (p) => {
      const i = p.tracks.findIndex((x) => x.id === t.id), n = p.tracks.length;
      const nt = kind === 'audio'
        ? emptyTrack(`Audio ${n + 1}`, 'audio', COLORS[n % COLORS.length], { input: { kind: 'browser' } })
        : emptyTrack(`MIDI ${n + 1}`, 'midi', COLORS[n % COLORS.length], { instrument: { plugin: 'srs:synth', preset: 'keys' } });
      if (t.folderId) nt.folderId = t.folderId;
      p.tracks.splice(i + 1, 0, nt); nid = nt.id;
    });
    useStudio.getState().select([], nid);
  });
  const inFolder = sel.every((x) => x.folderId);
  return (
    <div className="ctxmenu trackmenu" ref={ref} style={{ left: Math.min(at.x, window.innerWidth - 300), top }}>
      <div className="mh">{many ? `${ids.length} Spuren` : t.name}<span className="t2" style={{ fontWeight: 500, fontSize: 11, marginLeft: 8 }}>{many ? '' : t.kind === 'midi' ? 'MIDI-Spur' : 'Audiospur'}</span></div>
      {!many && <button onClick={act(() => { const el = document.querySelector<HTMLInputElement>(`.th[data-tid="${t.id}"] .tn input`); el?.focus(); el?.select(); })}><span>Umbenennen</span></button>}
      <div className="swatches" title="Spurfarbe">
        {COLORS.map((c) => <button key={c} style={{ background: c }} className={sel.every((x) => x.color === c) ? 'on' : ''} title="Farbe" onClick={act(() => commit('Spurfarbe', (p) => { for (const x of p.tracks) if (ids.includes(x.id)) x.color = c; }))} />)}
      </div>
      <small>Spur</small>
      <button onClick={act(() => run('dupTracks'))}><span>{many ? 'Spuren duplizieren' : 'Duplizieren'}</span><kbd>⌥⇧D</kbd></button>
      <button onClick={addBelow('audio')}><span>Neue Audiospur darunter</span></button>
      <button onClick={addBelow('midi')}><span>Neue MIDI-Spur darunter</span></button>
      <button onClick={act(() => commit(many ? 'Spuren ausblenden' : 'Spur ausblenden', (p) => { for (const x of p.tracks) if (ids.includes(x.id)) x.hidden = true; }))}><span>Ausblenden</span><kbd>spielt weiter</kbd></button>
      {inFolder
        ? <button onClick={act(() => commit('Aus Ordner nehmen', (p) => { for (const x of p.tracks) if (ids.includes(x.id)) x.folderId = undefined; if (p.folders) p.folders = p.folders.filter((f) => p.tracks.some((y) => y.folderId === f.id)); }))}><span>Aus dem Ordner nehmen</span></button>
        : <button onClick={act(() => run('folder'))}><span>In Ordner legen</span><kbd>⌥⇧F</kbd></button>}
      <small>Mix</small>
      <label className="tmrow"><span>Ausgang</span>
        <select value={sel.every((x) => x.output === sel[0].output) ? sel[0].output ?? '' : '?'} onChange={(e) => { const v = e.target.value; onClose(); if (v === 'new') { let bid = ''; commit('Neuer Bus für Auswahl', (q) => { bid = addBus(q, sel.length > 1 && /drum|kick|snare/i.test(sel.map((x) => x.name).join(' ')) ? 'Drums' : undefined); routeTracks(q, ids, bid); }); toast('Neuer Aux-Bus angelegt, die Spuren laufen darüber (Mixer: X)'); } else commit(v ? 'Ausgang Bus' : 'Ausgang Master', (q) => routeTracks(q, ids, v || undefined)); }}>
          <option value="">Master</option>{bussesOf(project).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}<option value="new">Neuer Bus …</option>{!sel.every((x) => x.output === sel[0].output) && <option value="?" disabled>verschieden</option>}
        </select>
      </label>
      {many && <button onClick={act(() => run('group'))}><span>Gruppe aus Auswahl</span><kbd>⌘G</kbd></button>}
      {sel.some((x) => x.frozen) ? <button onClick={act(() => unfreezeTracks(ids))}><span>Auftauen</span><kbd>Inserts wieder live</kbd></button>
        : <button onClick={act(() => { toast('Friere ein …'); void freezeTracks(ids).then((n) => toast(n ? `${n} ${n === 1 ? 'Spur' : 'Spuren'} eingefroren: spielen die gerechnete Fassung, Plugins und Instrument ruhen. Ändert ihr etwas, spielt die Spur wieder live.` : 'Keine Clips zum Einfrieren')); })}><span>Einfrieren</span><kbd>spart Rechenleistung</kbd></button>}
      <button onClick={act(() => useEdit.getState().open('commit', ids.join(',')))}><span>Commit: auf neue Audiospur rechnen …</span></button>
      {sel.every((x) => x.kind === 'audio') && <>
        <small>Elastic Audio</small>
        <div className="elrow">
          <button className={sel.every((x) => !x.elastic) ? 'on' : ''} title="Audio spielt 1:1 (zeitbasiert)" onClick={act(() => { const warped = project.clips.some((c) => ids.includes(c.trackId) && c.warp && (c.warp.points.length > 2 || c.warp.pitch)); commit('Elastic aus', (q) => ids.forEach((id) => setElastic(q, id, null))); if (warped) toast('Elastic aus: Warp und Tonhöhe verworfen. Rückgängig mit ⌘Z.'); })}>Aus</button>
          {STRETCH_MODES.map((m) => <button key={m.id} className={sel.every((x) => x.elastic === m.id) ? 'on' : ''} title={m.hint} onClick={act(() => { commit(`Elastic: ${m.label}`, (q) => ids.forEach((id) => setElastic(q, id, m.id))); useEdit.getState().set({ warp: [...new Set([...useEdit.getState().warp, ...ids])] }); })}>{m.label}</button>)}
        </div>
      </>}
      <div className="sep" />
      <button className="danger wide" onClick={act(() => removeTracks(ids))}><span>{many ? `${ids.length} Spuren löschen` : 'Spur löschen'}</span><kbd>mit Clips, ⌘Z</kbd></button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spurköpfe mit Playlist-Auswahl und Lanes
/** Spur im Spurkopf auswählen wie in Pro Tools: Klick = nur diese, ⌘/⌃-Klick = dazu/heraus, ⇧-Klick = Bereich */
let lastTrackClick: string | null = null;
function clickTrack(id: string, e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean }) {
  const st = useStudio.getState(), E = useEdit.getState(), cur = E.range;
  const order = st.project.tracks.filter((t) => t.kind !== 'bus').map((t) => t.id);
  const base = cur ? { start: cur.start, end: cur.end } : { start: engine.position, end: engine.position };
  const now = (cur?.trackIds ?? []).filter((x) => !x.includes(':'));
  let ids: string[];
  if (e.metaKey || e.ctrlKey) ids = now.includes(id) ? now.filter((x) => x !== id) : [...now, id];
  else if (e.shiftKey && (lastTrackClick || now.length)) {
    const a = order.indexOf(lastTrackClick ?? now[0]), b = order.indexOf(id);
    ids = [...new Set([...now, ...order.slice(Math.min(a, b), Math.max(a, b) + 1)])];
  } else ids = [id];
  ids = order.filter((x) => ids.includes(x));
  E.setRange(ids.length ? { ...base, trackIds: ids } : null);
  st.select([], ids.includes(id) ? id : ids[0] ?? null);
  lastTrackClick = id;
  if (ids.length > 1) toast(`${ids.length} Spuren ausgewählt`);
}

function TrackHead({ t, h, selected, lanesOpen, inserts, nested }: { t: Track; h: number; selected: boolean; lanesOpen: boolean; inserts: boolean; nested?: boolean }) {
  const commit = useStudio((s) => s.commit);
  const select = useStudio((s) => s.select);
  const clips = useStudio((s) => s.project.clips);
  const pl = useStudio((s) => s.project.playlists?.[t.id]);
  const takes = useStudio((s) => s.project.takes.filter((x) => x.trackId === t.id).length);
  const [menu, setMenu] = useState(false);
  const ai = clips.some((c) => c.trackId === t.id && c.by.ai);
  const upd = (label: string, f: (x: Track) => void, merge = false) => commit(label, (p) => f(p.tracks.find((y) => y.id === t.id)!), { merge });
  const io = t.kind === 'midi'
    ? (t.instrument?.plugin.startsWith('bridge:') ? t.instrument.plugin.slice(7) : ai ? 'SRS Engine' : `Instr. ${t.instrument?.preset ?? t.instrument?.plugin.replace('srs:', '')}`)
    : inputLabel(t) + (t.input?.kind === 'bridge' ? ' (Bridge)' : ' (dein Interface)');
  const lanes = (pl?.others.length ?? 0) + (t.kind === 'audio' ? takes : 0);
  const toggleLanes = () => { const e = useEdit.getState(); e.set({ lanes: lanesOpen ? e.lanes.filter((x) => x !== t.id) : [...e.lanes, t.id] }); };
  const autoOpen = useEdit((s) => (s.autoLanes[t.id]?.length ?? 0) > 0);
  const warpOn = useEdit((s) => s.warp.includes(t.id));
  const pj = useStudio((s) => s.project);
  const toggleAuto = () => {
    const e = useEdit.getState();
    const used = Object.entries(t.automation?.lanes ?? {}).filter(([, l]) => l.length).map(([k]) => k);
    e.set({ autoLanes: { ...e.autoLanes, [t.id]: autoOpen ? [] : used.length ? used : ['volume'] } });
  };
  const compact = h < 60;
  return (
    <div className={`th ${selected ? 'sel' : ''} ${compact ? 'compact' : ''} ${nested ? 'nested' : ''}`} style={{ height: h, gridTemplateColumns: `4px 1fr ${inserts ? '80px ' : ''}12px` }}
      onClick={(e) => { if ((e.target as HTMLElement).closest('input, button, select, .ic')) { select([], t.id); return; } clickTrack(t.id, e); }}
      data-tid={t.id}
      onContextMenu={(e) => { e.preventDefault(); if ((e.target as HTMLElement).closest('.ic')) return; openTrackMenu(t.id, e); }}>
      <div className="cs" style={{ background: t.color }} />
      <div className="tb">
        <div className="tn">
          <input value={t.name} onChange={(e) => upd('Spur umbenennen', (x) => { x.name = e.target.value; }, true)} title="Spurname" />
          {ai && <span className="tag">KI</span>}
          {groupsOfTrack(pj, t.id).length > 0 && <span className="grpb">{groupsOfTrack(pj, t.id).map((g) => <i key={g.id} style={{ background: g.color, opacity: g.active ? 1 : 0.45 }} title={`Gruppe ${g.name}${g.mix ? ' · Mix' : ''}${g.edit ? ' · Edit' : ''}${g.active ? '' : ' (aus)'}`}>{g.name.slice(0, 1)}</i>)}</span>}
          {t.frozen && <span className="tag fz" title={isFrozen(pj, t) ? 'Eingefroren: spielt die gerechnete Fassung (Spurmenü → Auftauen)' : 'Eingefroren, aber inzwischen geändert: spielt wieder live. Spurmenü → neu einfrieren'}>{isFrozen(pj, t) ? '❄' : '❄?'}</span>}
          {t.elastic && <span className="tag el" title={`Elastic Audio: ${STRETCH_MODES.find((m) => m.id === t.elastic)?.label}. Audio folgt dem Tempo.`}>◇</span>}
          <span className={`kind ${t.kind}`} title={`${t.kind === 'midi' ? 'MIDI-Spur' : 'Audiospur'} · Eingang: ${io}${t.kind === 'audio' ? ' (Klick: Audio-Setup)' : ''}`} onClick={(e) => { if (t.kind === 'audio') { e.stopPropagation(); useStudio.getState().setView('dante'); } }}>{t.kind === 'midi' ? 'MIDI' : inputLabel(t)}</span>
        </div>
        <div className="row">
          {t.kind !== 'bus' ? <button className={`rsm r ${t.armed ? 'on' : ''}`} title={t.kind === 'midi' ? 'Aufnahmebereit für das MIDI-Keyboard (R)' : 'Aufnahmebereit (R)'} onClick={() => { upd('Scharf schalten', (x) => { x.armed = !x.armed; }); if (t.kind === 'midi' && !t.armed) void midiIn.enable(); }}><i className="dot" /></button> : <span className="rsm ph" />}
          {t.kind === 'audio' && (t.input?.kind ?? 'browser') === 'browser' && <MonitorBtn t={t} />}
          <button className={`rsm s ${t.solo ? 'on' : ''}`} title="Solo" onClick={() => toggleSolo(t)}>S</button>
          <button className={`rsm m ${t.mute ? 'on' : ''}`} title="Mute" onClick={() => toggleMuteWrite(t)}>M</button>
          <input className="hsl" type="range" min={-48} max={6} step={0.5} value={t.volumeDb} title={`Lautstärke ${fmtAuto('volume', t.volumeDb)}`}
            onPointerDown={() => touchGrouped(t, 'volume', true)} onPointerUp={() => touchGrouped(t, 'volume', false)}
            onChange={(e) => setVolumeGrouped(t, Number(e.target.value))} />
          <span className="vdb mono">{t.volumeDb <= -47.9 ? '−∞' : `${t.volumeDb > 0 ? '+' : t.volumeDb < 0 ? '−' : ''}${Math.abs(t.volumeDb).toFixed(1).replace('.', ',')}`}</span>
        </div>
        {!compact && <div className="row io">
          <AutoModeSelect t={t} small />
          <span className={`lanebtn auto ${autoOpen ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); toggleAuto(); }} title="Automation-Kurven zeigen (Lautstärke, Panorama, Mute, Sends, Plugins)">Kurven</span>
          <span className="plsel" onClick={(e) => { e.stopPropagation(); setMenu(!menu); }} title="Playlists dieser Spur">{pl?.active ?? t.name} ▾</span>
          {t.elastic && <span className={`lanebtn warp ${warpOn ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); const E = useEdit.getState(); E.set({ warp: warpOn ? E.warp.filter((x) => x !== t.id) : [...E.warp, t.id] }); }} title={`Elastic Audio (${STRETCH_MODES.find((m) => m.id === t.elastic)?.label}): Warp-Ansicht mit Event- und Warp-Markern. Doppelklick setzt einen Marker, ziehen dehnt, ⌥-Klick löscht.`}>Warp</span>}
          {lanes > 0 && <span className={`lanebtn ${lanesOpen ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); toggleLanes(); }} title="Takes und Playlists als Lanes zeigen (Comping)">{lanesOpen ? '▴' : '▾'} {lanes}</span>}
        </div>}
      </div>
      {inserts && <InsertCol t={t} h={h} />}
      <MiniMeter id={t.id} />
      {menu && <PlaylistMenu t={t} onClose={() => setMenu(false)} />}
    </div>
  );
}

/** Inserts A–E am Spurkopf (wie die Insert-Spalte im Edit-Fenster von Pro Tools) */
function InsertCol({ t, h }: { t: Track; h: number }) {
  const openPlugin = useStudio((s) => s.openPlugin);
  const win = useStudio((s) => s.pluginWin);
  const [menu, setMenu] = useState<PluginMenuState>(null);
  const n = h < 60 ? 3 : 5;
  return (
    <div className="ic">
      {Array.from({ length: n }, (_, i) => {
        const s = t.inserts[i], def = s ? pluginDef(s.plugin) : undefined, open = !!s && win?.slotId === s.id;
        return (
          <div key={i} className={`is ${s ? 'filled' : ''} ${s?.bypass ? 'byp' : ''} ${s?.ai ? 'ai' : ''} ${open ? 'open' : ''}`}
            title={s ? `${SLOT_LETTERS[i]}: ${def?.name ?? s.plugin.replace('bridge:', '')}${s.bypass ? ' (Bypass)' : ''}. Klick öffnet, Rechtsklick für mehr` : `Insert ${SLOT_LETTERS[i]}: Plugin einfügen`}
            onClick={(e) => { e.stopPropagation(); if (s) openPlugin(open ? null : { channel: t.id, slotId: s.id }); else if (i <= t.inserts.length) setMenu({ channel: t.id, index: t.inserts.length, x: e.clientX, y: e.clientY }); }}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (s) setMenu({ channel: t.id, index: i, x: e.clientX, y: e.clientY, replace: s.id, actions: true }); }}>
            {s ? <>{def?.short ?? slotLabel(s)}{s.ai && <em>KI</em>}</> : i === t.inserts.length ? <span className="plus">+</span> : null}
          </div>
        );
      })}
      {menu && <PluginMenu menu={menu} close={() => setMenu(null)} />}
    </div>
  );
}

/** Spurliste (Pro Tools: Tracks-Liste): Ein- und Ausblenden, Auswahl, Reihenfolge per Ziehen */
function TrackList() {
  const tracks = useStudio((s) => s.project.tracks);
  const folders = useStudio((s) => s.project.folders);
  const sel = useStudio((s) => s.selection.trackId);
  const commit = useStudio((s) => s.commit);
  const [dragId, setDragId] = useState<string | null>(null);
  const list = tracks.filter((t) => t.kind !== 'bus');
  const move = (id: string, before: string) => commit('Spuren umsortieren', (p) => {
    const i = p.tracks.findIndex((t) => t.id === id); if (i < 0 || id === before) return;
    const [t] = p.tracks.splice(i, 1); const j = p.tracks.findIndex((x) => x.id === before);
    p.tracks.splice(j < 0 ? p.tracks.length : j, 0, t);
  });
  return (
    <div className="tlist">
      <div className="tlh"><b>Spuren</b><span className="mono">{list.filter((t) => !t.hidden).length}/{list.length}</span>
        <button className="lnk" title="Alle zeigen" onClick={() => commit('Alle Spuren zeigen', (p) => { for (const t of p.tracks) t.hidden = false; })}>alle</button>
        <button className="lnk" title="Spurliste schließen" onClick={() => useEdit.getState().setView({ tracks: false })}>✕</button></div>
      <div className="tlb">
        {list.map((t, i) => {
          const f = t.folderId ? folders?.find((x) => x.id === t.folderId) : undefined;
          const head = f && list[i - 1]?.folderId !== f.id;
          return (<Fragment key={t.id}>
          {head && <div className="tlf" onClick={() => commit(f.open ? 'Ordner zu' : 'Ordner auf', (p) => { const x = p.folders?.find((y) => y.id === f.id); if (x) x.open = !x.open; })}><span>{f.open ? '▾' : '▸'}</span><i style={{ background: f.color }} /><b>{f.name}</b></div>}
          <div className={`tli ${f ? 'inf' : ''} ${t.hidden ? 'hid' : ''} ${sel === t.id ? 'sel' : ''} ${dragId === t.id ? 'drag' : ''}`} draggable
            onDragStart={(e) => { setDragId(t.id); e.dataTransfer.setData('application/x-srs-track', t.id); e.dataTransfer.effectAllowed = 'move'; }}
            onDragEnd={() => setDragId(null)}
            onDragOver={(e) => { if ([...e.dataTransfer.types].includes('application/x-srs-track')) { e.preventDefault(); e.stopPropagation(); } }}
            onDrop={(e) => { const id = e.dataTransfer.getData('application/x-srs-track'); if (id) { e.preventDefault(); e.stopPropagation(); move(id, t.id); } setDragId(null); }}
            onClick={() => useStudio.getState().select([], t.id)}
            onContextMenu={(e) => { e.preventDefault(); openTrackMenu(t.id, e); }}>
            <button className="eye" title={t.hidden ? 'Spur zeigen' : 'Spur ausblenden (spielt weiter)'} onClick={(e) => { e.stopPropagation(); commit(t.hidden ? 'Spur zeigen' : 'Spur ausblenden', (p) => { const x = p.tracks.find((y) => y.id === t.id); if (x) x.hidden = !x.hidden; }); }}>{t.hidden ? '○' : '●'}</button>
            <i style={{ background: t.color }} />
            <span className="nm">{t.name}</span>
            <small>{t.kind === 'midi' ? 'MIDI' : 'Audio'}</small>
          </div>
          </Fragment>);
        })}
      </div>
    </div>
  );
}

/** Ordner-Kopf: auf- und zuklappen, umbenennen, alle Spuren darin solo/stumm, auflösen */
function FolderHead({ f, kids, h, selected }: { f: Folder; kids: Track[]; h: number; selected: boolean }) {
  const commit = useStudio((s) => s.commit);
  const ids = kids.map((k) => k.id);
  const allMute = kids.length > 0 && kids.every((k) => k.mute), allSolo = kids.length > 0 && kids.every((k) => k.solo);
  const upd = (label: string, fn: (x: Folder) => void, merge = false) => commit(label, (p) => { const x = p.folders?.find((y) => y.id === f.id); if (x) fn(x); }, { merge });
  const selectAll = () => { const r = useEdit.getState().range; useEdit.getState().setRange({ start: r?.start ?? engine.position, end: r?.end ?? engine.position, trackIds: ids }); useStudio.getState().select([], ids[0] ?? null); };
  return (
    <div className={`th folder ${selected ? 'sel' : ''}`} style={{ height: h }} onClick={(e) => { if (!(e.target as HTMLElement).closest('input, button')) selectAll(); }}>
      <div className="cs" style={{ background: f.color }} />
      <div className="fh">
        <button className="ftog" title={f.open ? 'Ordner zuklappen' : 'Ordner aufklappen'} onClick={() => upd(f.open ? 'Ordner zu' : 'Ordner auf', (x) => { x.open = !x.open; })}>{f.open ? '▾' : '▸'}</button>
        <svg width="14" height="12" viewBox="0 0 14 12" className="ficon"><path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3l1.5 1.5h4.5A1.5 1.5 0 0 1 13 4v5.5A1.5 1.5 0 0 1 11.5 11h-9A1.5 1.5 0 0 1 1 9.5z" fill={f.color} opacity=".85" /></svg>
        <input value={f.name} onChange={(e) => upd('Ordner umbenennen', (x) => { x.name = e.target.value; }, true)} title="Ordnername" />
        <span className="fn mono">{kids.length}</span>
        <button className={`rsm s ${allSolo ? 'on' : ''}`} title="Alle Spuren im Ordner solo" onClick={() => commit('Ordner Solo', (p) => { for (const t of p.tracks) if (ids.includes(t.id)) t.solo = !allSolo; })}>S</button>
        <button className={`rsm m ${allMute ? 'on' : ''}`} title="Alle Spuren im Ordner stumm" onClick={() => commit('Ordner Mute', (p) => { for (const t of p.tracks) if (ids.includes(t.id)) t.mute = !allMute; })}>M</button>
        <button className="lnk" title="Ordner auflösen (die Spuren bleiben)" onClick={() => commit('Ordner auflösen', (p) => op.dissolveFolder(p, f.id))}>auflösen</button>
      </div>
    </div>
  );
}

function PlaylistMenu({ t, onClose }: { t: Track; onClose: () => void }) {
  const commit = useStudio((s) => s.commit);
  const pl = useStudio((s) => s.project.playlists?.[t.id]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const off = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    window.addEventListener('pointerdown', off, true);
    return () => window.removeEventListener('pointerdown', off, true);
  }, [onClose]);
  const showLanes = () => { const e = useEdit.getState(); if (!e.lanes.includes(t.id)) e.set({ lanes: [...e.lanes, t.id] }); };
  return (
    <div className="ctxmenu plmenu" ref={ref} onClick={(e) => e.stopPropagation()}>
      <div className="mh">Playlist: {pl?.active ?? t.name}</div>
      <button onClick={() => { commit('Neue Playlist', (p) => op.newPlaylist(p, t.id, false)); showLanes(); onClose(); }}><span>Neue Playlist (leer)</span></button>
      <button onClick={() => { commit('Playlist duplizieren', (p) => op.newPlaylist(p, t.id, true)); showLanes(); onClose(); }}><span>Playlist duplizieren</span></button>
      {(pl?.others.length ?? 0) > 0 && <small>Wechseln zu</small>}
      {pl?.others.map((o) => (
        <div key={o.id} className="plrow">
          <button onClick={() => { commit('Playlist wechseln', (p) => op.switchPlaylist(p, t.id, o.id)); onClose(); }}><span>{o.name}</span><kbd>{o.clips.length} Clips</kbd></button>
          <button className="danger" title="Playlist löschen" onClick={() => { commit('Playlist löschen', (p) => op.deletePlaylist(p, t.id, o.id)); }}>✕</button>
        </div>
      ))}
    </div>
  );
}

export const fmtAuto = (param: string, v: number, t?: Track): string => A.isInsParam(param) ? (() => { const d = A.paramInfo(param, t).def; return d ? fmtParam(d, v) : String(Math.round(v * 100) / 100); })() : param === 'pan' ? (Math.abs(v) < 0.005 ? 'Mitte' : v < 0 ? `L ${Math.round(-v * 100)}` : `R ${Math.round(v * 100)}`) : param === 'mute' ? (v >= 0.5 ? 'stumm' : 'an') : v <= -59.9 ? '−∞ dB' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1).replace('.', ',')} dB`;
const MODE_CLASS: Record<AutoMode, string> = { off: '', read: 'read', touch: 'touch', latch: 'latch', write: 'write' };

/** Mute-Taste: in Touch/Latch/Write während der Wiedergabe als Automationspunkt schreiben, sonst normal umschalten */
export function toggleMuteWrite(t: Track) {
  const st = useStudio.getState(), mode = t.automation?.mode ?? 'off';
  const to = !(A.isAutomated(t, 'mute') ? A.valueAt(A.points(t, 'mute'), engine.position, 'mute', 0) >= 0.5 : t.mute);
  if (st.transport.playing && ['touch', 'latch', 'write'].includes(mode)) {
    const at = engine.position;
    st.commit('Mute schreiben', (p) => { for (const id of groupMembers(p, t.id, 'mix')) { const x = p.tracks.find((y) => y.id === id)!; A.addPoint(x, 'mute', Math.max(0, at - 0.001), to ? 0 : 1); A.addPoint(x, 'mute', at, to ? 1 : 0); x.mute = to; } });
    return;
  }
  st.commit('Mute', (p) => groupToggle(p, t.id, 'mute'));
}
/** Solo, in der Mix-Gruppe gemeinsam */
export const toggleSolo = (t: Track) => useStudio.getState().commit('Solo', (p) => groupToggle(p, t.id, 'solo'));
/** Fader, in der Mix-Gruppe relativ; Automation-Touch für alle Mitglieder */
export const setVolumeGrouped = (t: Track, db: number) => useStudio.getState().commit('Lautstärke', (p) => groupVolume(p, t.id, db), { merge: true });
export const touchGrouped = (t: Track, param: string, on: boolean) => { for (const id of groupMembers(useStudio.getState().project, t.id, 'mix')) engine.touch(id, param, on); };

export function AutoModeSelect({ t, small }: { t: Track; small?: boolean }) {
  const commit = useStudio((s) => s.commit);
  const mode = t.automation?.mode ?? 'off';
  return (
    <select className={`input automode ${MODE_CLASS[mode]} ${small ? 'small' : ''}`} value={mode} title={A.AUTO_MODES.find((m) => m.id === mode)?.hint}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { const m = e.target.value as AutoMode; commit(`Automation: ${m}`, (p) => { const x = A.trackLike(p, t.id); if (x) { x.automation ??= { mode: m, lanes: {} }; x.automation.mode = m; } }); }}>
      {A.AUTO_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
    </select>
  );
}

function AutoHead({ t, param, h }: { t: Track; param: string; h: number }) {
  const busses = useStudio((s) => s.project.busses);
  const commit = useStudio((s) => s.commit);
  const lanesOf = useEdit((s) => s.autoLanes[t.id] ?? []);
  const params = A.autoParams(bussesOf({ busses }), t);
  const groups = [...new Set(params.map((p) => p.group ?? ''))];
  const pos = useFrameValue(() => A.valueAt(A.points(t, param), engine.position, param, A.staticValue(t, param)));
  const setLanes = (list: string[]) => { const e = useEdit.getState(); e.set({ autoLanes: { ...e.autoLanes, [t.id]: list } }); };
  const n = A.points(t, param).length;
  return (
    <div className="ah" style={{ height: h }}>
      <div className="cs" style={{ background: t.color, opacity: 0.35 }} />
      <div className="ahb">
        <div className="row" style={{ gap: 4 }}>
          {t.id === 'master' && <span className="mtag" title="Master-Automation">M</span>}
          <select className="input" value={param} onChange={(e) => setLanes(lanesOf.map((x) => (x === param ? e.target.value : x)))}>
            {!params.some((p) => p.id === param) && <option value={param}>{param.startsWith('ins:') ? 'Plugin entfernt' : param}</option>}
            {groups.map((g) => {
              const opts = params.filter((p) => (p.group ?? '') === g).map((p) => <option key={p.id} value={p.id} disabled={p.id !== param && lanesOf.includes(p.id)}>{g ? p.label.slice(g.length + 2) : p.label}</option>);
              return g ? <optgroup key={g} label={`Plugin ${g}`}>{opts}</optgroup> : opts;
            })}
          </select>
          <AutoModeSelect t={t} small />
        </div>
        <div className="row" style={{ gap: 4 }}>
          <span className="mono aval">{fmtAuto(param, pos, t)}</span>
          <span className="t2" style={{ fontSize: 10.5 }}>{n} Punkte</span>
          {n > 0 && <button className="lnk" title="Alle Punkte dieser Kurve löschen" onClick={() => commit('Automation leeren', (p) => { const x = A.trackLike(p, t.id); if (x?.automation) delete x.automation.lanes[param]; })}>leeren</button>}
          <button className="lnk" title="Weitere Kurve" onClick={() => { const next = params.find((p) => !lanesOf.includes(p.id)); if (next) setLanes([...lanesOf, next.id]); }}>+</button>
          <button className="lnk" title="Kurve ausblenden" onClick={() => setLanes(lanesOf.filter((x) => x !== param))}>✕</button>
        </div>
      </div>
    </div>
  );
}

/** Wert in jedem Frame neu lesen, aber nur neu zeichnen, wenn er sich ändert */
function useFrameValue(fn: () => number) {
  const [v, setV] = useState(fn);
  const ref = useRef(fn); ref.current = fn;
  useEffect(() => {
    let raf = 0, last = NaN;
    const loop = () => { const x = Math.round(ref.current() * 100) / 100; if (x !== last) { last = x; setV(x); } raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return v;
}

function LaneHead({ row, solo, onSolo, onComp }: { row: Extract<Row, { kind: 'lane' }>; solo: boolean; onSolo: () => void; onComp: () => void }) {
  return (
    <div className="lh" style={{ height: row.h }}>
      <div className="cs" style={{ background: row.t.color, opacity: 0.4 }} />
      <span className="ln">{row.lane.label}</span>
      <button className={`rsm s ${solo ? 'on' : ''}`} title="Diese Lane statt der Haupt-Playlist abhören" onClick={onSolo}>S</button>
      <button className="rsm up" title="Auswahl (oder ganze Lane) in die Haupt-Playlist kopieren (⌥⌃V)" onClick={onComp}>↑</button>
      {row.lane.takeId && <button className="rsm del" title={`${row.lane.label} löschen (auch aus der Timeline)`} onClick={() => removeTakes([row.lane.takeId!], row.lane.label)}>✕</button>}
    </div>
  );
}

export function MiniMeter({ id }: { id: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0, hold = 0;
    const loop = () => {
      const s = engine.stripOf(id);
      let v = 0;
      if (s) { s.meter.getFloatTimeDomainData(s.meterBuf as Float32Array<ArrayBuffer>); for (const x of s.meterBuf) v = Math.max(v, Math.abs(x)); }
      const db = v > 0 ? 20 * Math.log10(v) : -96;
      hold = Math.max(hold * 0.9, Math.max(0, Math.min(1, (db + 54) / 54)));
      ref.current?.querySelectorAll('i').forEach((el, k) => (el as HTMLElement).style.setProperty('--v', `${(hold * (k ? 0.94 : 1)) * 100}%`));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [id]);
  return <div className="mm" ref={ref}><i /><i /></div>;
}

// ---------------------------------------------------------------------------
// Zeichnen eines Clips: Waveform/Noten, Clip Gain, Fades, Loops, Stumm, Gesperrt
function drawClip(g: CanvasRenderingContext2D, c: Clip, t: Track, x: number, y: number, w: number, h: number, on: boolean, p: Project, W: number, zoom: number) {
  const ai = !!c.by.ai, col = c.muted ? '#8a93a6' : t.color, light = lum(col) > 0.62;
  // Körper in Spurfarbe, Namensleiste oben (wie Pro Tools), Auswahl blau umrandet
  roundRect(g, x + 0.5, y + 0.5, w - 1, h - 1, 4);
  g.fillStyle = mix(col, on ? 0.26 : 0.17); g.fill();
  g.save(); roundRect(g, x, y, w, h, 4); g.clip();
  const NB = h >= 34 ? 15 : 0;
  if (NB) { g.fillStyle = on ? '#003399' : mix(col, c.muted ? 0.5 : 0.92); g.fillRect(x, y, w, NB); }
  const label = (ai ? '✦ ' : '') + (c.loop ? '↻ ' : '') + c.name;
  g.font = '600 10.5px "IBM Plex Sans"'; g.fillStyle = NB ? (on || !light ? '#fff' : '#071026') : shade(col);
  g.fillText(label, x + 6, y + (NB ? 11 : 12));
  let lx = x + 12 + g.measureText(label).width;
  const tag = (txt: string, fg: string, bg: string) => { g.font = '600 9px "IBM Plex Mono"'; const tw = g.measureText(txt).width; roundRect(g, lx, y + 2, tw + 8, 11, 3); g.fillStyle = bg; g.fill(); g.fillStyle = fg; g.fillText(txt, lx + 4, y + 10.5); lx += tw + 11; };
  if (ai) tag('SRS KI', '#5b3fc4', '#f1edfb');
  if (c.muted) tag('stumm', '#5a6478', '#eef0f4');
  if (c.locked) tag('gesperrt', '#5a6478', '#eef0f4');
  const top = y + NB + 2, hh = h - NB - 4, mid = top + hh / 2;
  g.fillStyle = shade(col);
  const len = c.loop?.length && c.loop.length > 0 ? c.loop.length : c.duration;
  const iters = Math.ceil(c.duration / len - 1e-9);
  if (c.kind === 'midi' && c.notes?.length) {
    const lenB = beatAt(p, c.start + len) - beatAt(p, c.start);
    const vis = c.notes.filter((n) => n.t >= 0 && n.t < lenB - 1e-9);
    if (vis.length) {
      const ms = vis.map((n) => n.m), lo = Math.min(...ms), hi = Math.max(...ms), rows = hi - lo + 1, rh = Math.max(2, Math.min(5, hh / rows));
      for (let k = 0; k < iters; k++) {
        const is = c.start + k * len, b0 = beatAt(p, is), ie = is + len;
        for (const n of vis) {
          const ns = secAt(p, b0 + n.t), nx = x + (ns - c.start) * zoom;
          if (nx > x + w || nx > W) continue;
          g.fillRect(nx, top + 2 + (hi - n.m) * ((hh - 4 - rh) / Math.max(1, rows - 1)), Math.max(2, (Math.min(secAt(p, b0 + n.t + n.d), ie) - ns) * zoom - 1), rh);
        }
      }
    }
  } else if (c.kind === 'audio') {
    // Elastic Audio: gerechnete Fassung zeichnen; solange sie gerechnet wird, das Original schraffiert
    const el = c.warp ? resolveElastic(p, c) : null;
    const buf = el && el !== 'pending' ? engine.buffers.get(el.id) : c.sourceId ? engine.buffers.get(c.sourceId) : undefined;
    const off = el && el !== 'pending' ? el.src0 : c.offset;
    const gl = Math.pow(10, (c.gainDb ?? 0) / 20);
    if (buf) for (let k = 0; k < iters; k++) {
      const ix = x + k * len * zoom, iw = Math.min(len, c.duration - k * len) * zoom;
      if (ix > W) break;
      drawWave(g, buf, off, iw / zoom, ix, iw, mid, Math.min(hh / 2, (hh / 2) * gl), 0, W);
    }
    else { g.fillRect(x, mid, w, 1); g.font = '500 10px "IBM Plex Mono"'; g.fillText('lädt …', x + 8, mid - 4); }
    if (el === 'pending') {
      g.save(); g.globalAlpha = 0.5; g.strokeStyle = '#ffffff'; g.lineWidth = 1;
      for (let hx = Math.floor(x / 8) * 8 - hh; hx < x + w; hx += 8) { g.beginPath(); g.moveTo(hx, top + hh); g.lineTo(hx + hh, top); g.stroke(); }
      g.restore(); g.font = '600 9.5px "IBM Plex Mono"'; g.fillStyle = shade(col); g.fillText('Elastic wird berechnet …', x + 6, top + hh - 4);
    }
    // Clip Gain: Linie und Wert unten links (wie Pro Tools)
    if (h >= 44 && w > 46) {
      const gd = c.gainDb ?? 0, txt = `${gd > 0 ? '+' : gd < 0 ? '−' : ''}${Math.abs(gd).toFixed(1).replace('.', ',')} dB`;
      g.font = '600 9px "IBM Plex Mono"'; const tw = g.measureText(txt).width;
      roundRect(g, x + 4, y + h - 14, tw + 14, 11, 3); g.fillStyle = gd ? '#fcf4e2' : 'rgba(255,255,255,.75)'; g.fill();
      g.fillStyle = gd ? '#7a5200' : shade(col); g.beginPath(); g.moveTo(x + 7, y + h - 6); g.lineTo(x + 10, y + h - 11); g.lineTo(x + 13, y + h - 6); g.closePath(); g.fill();
      g.fillText(txt, x + 15, y + h - 5.5);
    }
  }
  // Loop-Durchgänge
  if (c.loop) for (let k = 1; k < iters; k++) { const lxp = x + k * len * zoom; g.fillStyle = mix(t.color, 0.5); g.fillRect(lxp, y, 1, h); g.beginPath(); g.moveTo(lxp - 4, y); g.lineTo(lxp + 4, y); g.lineTo(lxp, y + 5); g.fill(); }
  // Fades: Kurve und abgedunkelter Bereich außerhalb
  const fade = (from: number, dur: number, shape: Clip['fadeInShape'], inward: boolean) => {
    const fx = x + from * zoom, fw = dur * zoom;
    if (fw < 1) return;
    g.beginPath();
    for (let i = 0; i <= 24; i++) { const u = i / 24, gain = fadeCurve(shape, inward ? u : 1 - u); const px = fx + u * fw, py = y + h - gain * (h - 2); if (i) g.lineTo(px, py); else g.moveTo(px, py); }
    g.strokeStyle = shade(t.color); g.lineWidth = 1.2; g.stroke();
    g.lineTo(inward ? fx + fw : fx + fw, y); g.lineTo(inward ? fx : fx, y); g.closePath();
    g.fillStyle = 'rgba(7,16,38,.08)'; g.fill();
  };
  if (c.kind === 'audio' && c.fadeIn > 0) fade(0, c.fadeIn, c.fadeInShape, true);
  if (c.kind === 'audio' && c.fadeOut > 0) fade(c.duration - c.fadeOut, c.fadeOut, c.fadeOutShape, false);
  g.restore();
  g.setLineDash(ai ? [4, 3] : []);
  roundRect(g, x + 0.5, y + 0.5, w - 1, h - 1, 4);
  g.strokeStyle = on ? '#003399' : ai ? '#8f78d6' : mix(col, 0.7); g.lineWidth = on ? 2 : 1; g.stroke();
  g.setLineDash([]);
}

// ---------------------------------------------------------------------------
export const fmtBar = (p: Project, s: number) => { const x = bbt(p, s + 1e-7); return `${x.bar}.${x.beat}`; };
/** Takt.Schlag.Tick (1000 Ticks je Schlag), wie die Uhr oben; folgt Tempo- und Taktwechseln */
export function fmtBBT(p: Project, s: number) {
  const x = bbt(p, s + 1e-7);
  return `${x.bar}.${x.beat}.${String(x.tick).padStart(3, '0')}`;
}
/** Länge als Takte.Schläge.Ticks, gemessen ab der Stelle at (Taktart und Tempo dort) */
export function fmtLen(p: Project, s: number, at = 0) {
  const beats = beatAt(p, at + s) - beatAt(p, at) + 1e-7, m = meterAt(p, at), unit = 4 / m.den;
  const bars = Math.floor(beats / m.barLen), rest = (beats - bars * m.barLen) / unit;
  return `${bars}.${Math.floor(rest)}.${String(Math.floor((rest % 1) * 1000)).padStart(3, '0')}`;
}
/** Eingabe als Takt.Schlag.Tick („5“, „5.3“, „5.3.500“) oder Minuten:Sekunden („1:23,5“); Längen ab der Stelle at */
export function parseTime(p: Project, text: string, asLength = false, at = 0): number | null {
  const s = text.trim().replace(',', '.');
  const ms = /^(\d+):(\d+(?:\.\d+)?)$/.exec(s);
  if (ms) return Number(ms[1]) * 60 + Number(ms[2]);
  const m = /^(\d+)(?:\.(\d+))?(?:\.(\d+))?$/.exec(s);
  if (!m) return null;
  const bar = Number(m[1]), beat = Number(m[2] ?? (asLength ? 0 : 1)), tick = Number(m[3] ?? 0);
  if (!asLength) return secOfBBT(p, Math.max(1, bar), Math.max(1, beat), tick);
  const mt = meterAt(p, at), beats = bar * mt.barLen + (beat + tick / 1000) * (4 / mt.den);
  return secAt(p, beatAt(p, at) + beats) - at;
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  r = Math.max(0, Math.min(r, w / 2, h / 2)); g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}
const rgb = (hex: string) => { const n = parseInt(hex.slice(1), 16); return [n >> 16, (n >> 8) & 255, n & 255]; };
function mix(hex: string, a: number) { const [r, g, b] = rgb(hex); const m = (v: number) => Math.round(v * a + 255 * (1 - a)); return `rgb(${m(r)},${m(g)},${m(b)})`; }
function lum(hex: string) { const [r, g, b] = rgb(hex); return (0.299 * r + 0.587 * g + 0.114 * b) / 255; }
function shade(hex: string) { const [r, g, b] = rgb(hex); return `rgb(${Math.round(r * 0.75)},${Math.round(g * 0.75)},${Math.round(b * 0.75)})`; }
function safeKey(k: string) { try { return parseKey(k); } catch { return { tonic: 0, mode: 'major' as const }; } }
const ROMAN = ['I', 'II', 'II', 'III', 'III', 'IV', 'V', 'V', 'VI', 'VI', 'VII', 'VII'];
function roman(symbol: string, key: { tonic: number; mode: 'minor' | 'major' }) {
  try {
    const c = parseChord(symbol), deg = (c.root - key.tonic + 12) % 12;
    const minor = c.intervals.includes(3) && !c.intervals.includes(4);
    return minor ? ROMAN[deg].toLowerCase() : ROMAN[deg];
  } catch { return ''; }
}
// currentRange wird von außen (Dialoge) genutzt
export { currentRange };

/** Input Monitoring (Pro Tools: I): Eingang über die Spur und ihre Plugins abhören */
function MonitorBtn({ t }: { t: Track }) {
  const [, tick] = useState(0);
  useEffect(() => onInputChange(() => tick((n) => n + 1)), []);
  const on = isMonitoring(t.id);
  return <button className={`rsm i ${on ? 'on' : ''}`} title="Input Monitoring: Eingang über diese Spur abhören (mit Plugins). Für Gesang besser das Direct Monitoring des Interfaces, der Browser hört etwas verzögert."
    onClick={(e) => { e.stopPropagation(); void setMonitoring(t, !on).catch((err) => toast((err as Error).message)); }}>I</button>;
}
